import {
  Suspense, cloneElement, isValidElement, lazy, useCallback, useEffect, useId, useRef, useState,
  type ReactElement, type ReactNode,
} from 'react';
import { readConsoleMode, readConsoleTheme, readNavMode, readThemeMode } from '../ui-flags';
/* TICK-140 — the redesigned console, behind `?ui=v2`. See the branch in `Superadmin`'s render. */
import {
  AccessTokensScreen, AppShell, AuditLogScreen, CreditOperationsDestination, EventVendorKeysDestination,
  DeliveryOperationsDestination, EventsScreen, GlobalCalendarDestination, LeadsScreen,
  LineOaPlatformDestination, PlatformOverviewScreen, PortalCmsDestination,
  TransactionsDestination, VendorLogsDestination, destinationFor, navigationFor,
} from '../console';
import {
  api, superadminApi, superadminLineOaApi, superadminRolloutApi, ApiError, fmt,
  type AccessTokenDto, type AdminRevenueSeriesPointDto, type AuditRowDto, type ConcertCalendarDto,
  type ConcertCalendarImportResultDto, type CreateEventResultDto, type CreateTokenResultDto,
  type ConcertImportDraftDto, type DeliveryRolloutStatusDto, type EventSalesMode, type EventTemplateDto,
  type LineRolloutMode, type OrganizerLeadDto, type PlatformOverviewDto, type PortalSettingsDto,
  type NotificationDeliveryDto, type ProviderStatusDto, type RevenueSeriesRange, type ServiceStatusDto,
  type LineOaAuditEntryDto, type LineOaCheckDto, type LineOaConnectionDto, type LineOaStatusDto, type LineOaVerifyDto,
  type SlipVerificationAuditDto, type SuperadminOperationDto, type TenantSummaryDto,
  type VendorCaptureSettingsDto, type VendorExchangeDto, type EventVendorKeysDto, type EventVendorKeysPatchDto,
} from '../api';
import RevenueTrendChart from '../components/RevenueTrendChart';
import { SuperadminCreditsPanel } from '../components/SuperadminCreditsPanel';

/**
 * Mermaid weighs ~645 kB and this page ships in the main bundle, so the flow-map viewer is only
 * fetched once an operator actually opens that tab.
 */
const FlowMapsConsolePanel = lazy(() => import('../components/FlowMapsConsolePanel'));
const FlowMapsPanel = lazy(() => import('../components/FlowMapsPanel'));

/** TICK-042 — kept next to each other so the operator reads the consequence, not just the name. */
const SALES_MODE_LABEL: Record<EventSalesMode, string> = {
  public: 'ขายปกติ',
  uat: 'UAT (ทีมงานเท่านั้น)',
  closed: 'ปิดการขาย',
};
const SALES_MODE_HINT: Record<EventSalesMode, string> = {
  public: 'ใครก็เข้าถึงและซื้อบัตรได้ตามปกติ',
  uat: 'คนนอกทีมจะได้ 404 เหมือนงานนี้ไม่มีอยู่ — ใช้สำหรับทดสอบบน Production',
  closed: 'ยังเห็นหน้างานได้ แต่สั่งซื้อไม่ได้ (ยกเลิกออเดอร์ค้างได้)',
};
import ConcertCalendar from './ConcertCalendar';
import { pastDateWarning } from '../lib/eventDate';

type Tab = 'overview' | 'events' | 'homeCms' | 'calendar' | 'lineOa' | 'credits' | 'delivery' | 'leads' | 'tokens' | 'audit' | 'operations' | 'vendorLogs' | 'flowMaps';

const NAV: { key: Tab; label: string; group: string }[] = [
  { key: 'overview', label: 'ภาพรวมแพลตฟอร์ม', group: 'แพลตฟอร์ม' },
  { key: 'events', label: 'อีเวนต์ / Workspaces', group: 'แพลตฟอร์ม' },
  { key: 'homeCms', label: 'จัดการหน้าหลัก (Home CMS)', group: 'แพลตฟอร์ม' },
  { key: 'calendar', label: 'ปฏิทินงานคอนเสิร์ต', group: 'แพลตฟอร์ม' },
  { key: 'lineOa', label: 'LINE OA', group: 'แพลตฟอร์ม' },
  { key: 'credits', label: 'เครดิตผู้จัดงาน', group: 'แพลตฟอร์ม' },
  { key: 'leads', label: 'Leads from Home Page', group: 'การเข้าถึง' },
  { key: 'tokens', label: 'ผู้ใช้ & Token', group: 'การเข้าถึง' },
  { key: 'audit', label: 'บันทึกการใช้งาน', group: 'การเข้าถึง' },
  /*
   * TICK-093 — its own destination, not a card inside LINE OA.
   *
   * It began as the LINE kill switch and lived where LINE lives. It now also holds the only lever
   * over `/myticket`, which has nothing to do with LINE — and an emergency switch filed under an
   * unrelated feature is one nobody finds during the emergency it exists for.
   */
  { key: 'delivery', label: 'สวิตช์การส่งมอบตั๋ว', group: 'ระบบ' },
  { key: 'operations', label: 'ธุรกรรม & การส่งบัตร', group: 'ระบบ' },
  { key: 'vendorLogs', label: 'การตอบกลับจากผู้ให้บริการ', group: 'ระบบ' },
  { key: 'flowMaps', label: 'แผนผังการทำงานของระบบ', group: 'ระบบ' },
];

const PAGE_META: Record<Tab, { title: string; sub: string }> = {
  overview: { title: 'ภาพรวมแพลตฟอร์ม', sub: 'รวมทุกอีเวนต์ที่รันอยู่บนแพลตฟอร์ม' },
  credits: { title: 'เครดิตผู้จัดงาน', sub: 'ยอดเครดิต ยอดค้างจ่าย การให้เครดิต และการตรวจรายการเติมเงิน — ทุกการกระทำถูกบันทึกใน audit log' },
  events: { title: 'อีเวนต์ / Workspaces', sub: 'แต่ละอีเวนต์คือ workspace แยกกัน มีหน้าขายและทีมงานของตัวเอง' },
  homeCms: { title: 'จัดการหน้าหลัก (Home CMS)', sub: 'ตั้งค่าการแสดงผล ลำดับ แบนเนอร์ และส่วนสำหรับผู้จัดงาน บนหน้า Portal' },
  calendar: { title: 'ปฏิทินงานคอนเสิร์ต', sub: 'ตารางงานคอนเสิร์ตและแฟนมีตติ้งทั่วประเทศไทย — ข้อมูลภายในสำหรับทีมงานเลี่ยงวันชนกัน ไม่เปิดสาธารณะ' },
  lineOa: { title: 'LINE OA', sub: 'LINE OA ของ JamTick และของผู้จัดแต่ละงาน — สถานะ การตรวจสอบ และการเปลี่ยน credential' },
  leads: { title: 'Leads from Home Page', sub: 'รายชื่อผู้จัดงานที่ส่งข้อมูลติดต่อกลับมาจากหน้าแรก' },
  tokens: { title: 'ผู้ใช้ & Token', sub: 'ออก Token การเข้าถึง แยกตามอีเวนต์และบทบาท · เพิกถอนได้ทุกเมื่อ' },
  audit: { title: 'บันทึกการใช้งาน', sub: 'กิจกรรมทั้งหมดข้ามอีเวนต์ — ใครทำอะไร ที่ไหน เมื่อไร' },
  delivery: { title: 'สวิตช์การส่งมอบตั๋ว', sub: 'สวิตช์ระดับแพลตฟอร์มที่ปิดได้ทันทีโดยไม่ต้อง deploy — ช่องทาง LINE และหน้า /myticket' },
  operations: { title: 'ธุรกรรม & การส่งบัตร', sub: 'ติดตามผลตรวจสอบการชำระเงินและสถานะส่งบัตรในปลายทางเดียว' },
  vendorLogs: { title: 'การตอบกลับจากผู้ให้บริการ', sub: 'คำตอบที่ Thunder / EasySlip / ThaiBulkSMS / Resend ส่งกลับมาจริง — ใช้ตรวจสอบเมื่อระบบให้ผลไม่ตรงกับที่คาด' },
  flowMaps: { title: 'แผนผังการทำงานของระบบ', sub: 'ลำดับการทำงานจริงของแต่ละฟีเจอร์ อ่านจาก docs/flow-maps โดยตรง — ใช้ไล่ปัญหาและอธิบายระบบให้ทีมใหม่' },
};

/*
 * The same site map applied to this console: thirteen tabs become six destinations, everything
 * else a section tab inside one. Behind the same `?nav=phases` as the organizer console, because a
 * person reviewing the shape should get one answer from both, not a half-converted product.
 *
 * The axis differs, and deliberately. Event Admin's destinations are the phases of an event's life,
 * and they carry numbers because that *is* a sequence — the same four `PhaseKey`s readiness counts.
 * This console is not attached to one event and its work has no order: these are areas of
 * responsibility. Numbering them would state a sequence that does not exist, so they are unnumbered.
 *
 * `delivery` stays out of Portal & Integrations on purpose. TICK-093 moved it out of LINE OA after
 * it grew the only lever over `/myticket`, on the grounds that an emergency switch filed under an
 * unrelated feature is one nobody finds during the emergency. Filing it back beside LINE OA would
 * undo that; it sits with the transactions it actually governs.
 */
type SuperDest = 'today' | 'events' | 'commerce' | 'access' | 'platform' | 'reliability';

const NAV_PHASES: { key: SuperDest; label: string; icon: ReactNode; items: Tab[] }[] = [
  { key: 'today', label: 'ภาพรวมแพลตฟอร์ม', icon: <IconGrid />, items: ['overview'] },
  { key: 'events', label: 'อีเวนต์ / Workspaces', icon: <IconLayers />, items: ['events'] },
  { key: 'commerce', label: 'ธุรกรรมและการส่งบัตร', icon: <IconCheck />, items: ['operations', 'delivery'] },
  { key: 'access', label: 'การเข้าถึงและบันทึก', icon: <IconKey />, items: ['tokens', 'audit', 'leads'] },
  { key: 'platform', label: 'Portal และการเชื่อมต่อ', icon: <IconHome />, items: ['homeCms', 'calendar', 'lineOa', 'credits'] },
  { key: 'reliability', label: 'ความน่าเชื่อถือของระบบ', icon: <IconServer />, items: ['vendorLogs', 'flowMaps'] },
];

/**
 * A glyph per tab for the v2 sidebar, where every tab is its own sidebar item rather than a section
 * inside a destination. `NAV_PHASES` only carries six — one per destination — and three items
 * sharing a key icon is unreadable in a 72px rail, so the tabs that need their own get one here.
 */
const TAB_ICONS: Record<Tab, ReactNode> = {
  overview: <IconGrid />,
  events: <IconLayers />,
  operations: <IconCheck />,
  /* A kill switch gets the stop glyph — it is the one control on this console that ends a sale. */
  delivery: <IconStop />,
  tokens: <IconKey />,
  audit: <IconAudit />,
  leads: <IconPhone />,
  homeCms: <IconHome />,
  calendar: <IconCalendar />,
  lineOa: <IconChat />,
  credits: <IconBookmark />,
  vendorLogs: <IconServer />,
  flowMaps: <IconFlow />,
};

/** The section tab's own name — shorter than `PAGE_META.title` where the destination already says it. */
const SECTION_LABEL: Partial<Record<Tab, string>> = {
  operations: 'ธุรกรรม & การส่งบัตร',
  delivery: 'สวิตช์การส่งมอบตั๋ว',
  tokens: 'ผู้ใช้ & Token',
  audit: 'บันทึกการใช้งาน',
  leads: 'Leads จากหน้าแรก',
  homeCms: 'หน้าหลัก (Home CMS)',
  calendar: 'ปฏิทินงานคอนเสิร์ต',
  lineOa: 'LINE OA',
  credits: 'เครดิตผู้จัดงาน',
  vendorLogs: 'การตอบกลับจากผู้ให้บริการ',
  flowMaps: 'แผนผังการทำงานของระบบ',
};

/** The thirteen tabs as the console has always listed them — exported so the test can assert the
 *  six destinations cover exactly this, rather than a list typed out a second time in the test. */
export const SUPER_NAV_CLASSIC: readonly { key: Tab; label: string; group: string }[] = NAV;

/** The six destinations with their section tabs. Exported so the menu can be tested as data. */
export function superNavPhases(): { key: SuperDest; label: string; icon: ReactNode; items: { key: Tab; label: string }[] }[] {
  const byKey = new Map(NAV.map((n) => [n.key, n]));
  return NAV_PHASES.map((d) => ({
    key: d.key,
    label: d.label,
    icon: d.icon,
    items: d.items.map((k) => ({ key: k, label: SECTION_LABEL[k] ?? byKey.get(k)?.label ?? k })),
  }));
}

const ROLE_LABEL: Record<AccessTokenDto['role'], string> = { owner: 'Owner', staff: 'Staff', checkin: 'Check-in', sales: 'การขาย' };
const STATUS_LABEL: Record<TenantSummaryDto['status'], string> = { active: 'กำลังขาย', paused: 'พักการขาย', archived: 'เก็บถาวร' };

/* ---------- icons ---------- */
function IconGrid({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect>
      <rect x="14" y="14" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect>
    </svg>
  );
}
function IconLayers({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline>
    </svg>
  );
}
function IconKey({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="4.5"></circle><path d="m10.5 12.5 8-8"></path><path d="m16 7 2 2"></path><path d="m19 4 2 2"></path>
    </svg>
  );
}
function IconAudit({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline>
      <line x1="8" y1="13" x2="16" y2="13"></line><line x1="8" y1="17" x2="13" y2="17"></line>
    </svg>
  );
}
function IconServer({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="8" rx="2"></rect><rect x="2" y="14" width="20" height="8" rx="2"></rect>
      <line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line>
    </svg>
  );
}
function IconSidebar({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2"></rect><line x1="9.5" y1="4" x2="9.5" y2="20"></line>
    </svg>
  );
}
function IconFlow({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8.5" y="2.5" width="7" height="5" rx="1"></rect>
      <rect x="2" y="16.5" width="7" height="5" rx="1"></rect>
      <rect x="15" y="16.5" width="7" height="5" rx="1"></rect>
      <path d="M12 7.5v4M12 11.5H5.5v5M12 11.5h6.5v5"></path>
    </svg>
  );
}
function IconHome({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );
}
function IconCalendar({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  );
}
function IconPhone({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );
}
function IconChat({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
  );
}
function IconPlus({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );
}
function IconX({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}
function IconCheck({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}
function IconTrash({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle><line x1="4.9" y1="4.9" x2="19.1" y2="19.1"></line>
    </svg>
  );
}
function IconBookmark({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21 12 16.5 5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
    </svg>
  );
}
function IconRefresh({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
  );
}
function ToggleSwitch({
  checked, onChange, disabled, title,
}: { checked: boolean; onChange: () => void; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={disabled}
      onClick={onChange}
      style={{
        flex: 'none', width: 38, height: 22, padding: 0, borderRadius: 999, border: 'none',
        position: 'relative', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        background: checked ? 'var(--sv-ok-soft)' : 'rgba(255,255,255,0.15)', transition: 'background 0.15s',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: '50%',
          background: '#fff', transition: 'left 0.15s',
        }}
      />
    </button>
  );
}
function IconStop({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="5" y="5" width="14" height="14" rx="2"></rect>
    </svg>
  );
}
function IconPlay({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="6 4 20 12 6 20 6 4"></polygon>
    </svg>
  );
}
function IconEye({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}
function IconEyeOff({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
      <path d="M6.61 6.61C3.35 8.36 2 12 2 12s3.5 7 10 7a9.26 9.26 0 0 0 5.39-1.61"></path>
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"></path><line x1="2" y1="2" x2="22" y2="22"></line>
    </svg>
  );
}
function IconCopy({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  );
}
function IconLogout({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline>
      <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
  );
}

/* ---------- shared bits ---------- */
function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="admin-revenue-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

/*
 * A labelled field in the SuperAdmin modals.
 *
 * The caption used to be a plain `<div>` with nothing tying it to the control beneath it, so every
 * input and select in these dialogs had **no accessible name at all** — a screen reader read an
 * unlabelled combobox, and a test could only reach them by position. TICK-142 found it the second
 * way: `locator('select').first()`.
 *
 * It is not a `<label>` wrapper, which would be the obvious fix and the wrong one here: one of
 * these fields wraps the role picker, a grid of buttons, and wrapping that in a label makes a click
 * on the caption press the first button. So the caption gets an id and the child is given
 * `aria-labelledby` instead, which names the control without changing what a click does.
 *
 * A child that already carries its own `aria-label` keeps it — the date input does — and only a
 * single element child is touched, because anything else has no one control to name.
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const labelId = `${useId()}-label`;
  const named = isValidElement(children)
    && !(children.props as { 'aria-label'?: string })['aria-label']
    && !(children.props as { 'aria-labelledby'?: string })['aria-labelledby']
    ? cloneElement(children as ReactElement<{ 'aria-labelledby'?: string }>, { 'aria-labelledby': labelId })
    : children;
  return (
    <div className="sa-field">
      <div className="sa-field-label" id={labelId}>{label}</div>
      {named}
    </div>
  );
}

function Modal({ title, sub, onClose, children }: { title: string; sub: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sa-modal-head">
          <div>
            <div className="title">{title}</div>
            <div className="sub">{sub}</div>
          </div>
          <button className="sa-icon-btn" onClick={onClose} title="ปิด"><IconX /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- overview ---------- */
/** Whole calendar days since the event was created on the platform — the closest proxy to "days on sale" we have. */
function daysSince(isoDate: string): number {
  const ms = Date.now() - new Date(isoDate).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function statusText(status: ServiceStatusDto['status'] | ProviderStatusDto['availability']): string {
  return status === 'operational' ? 'พร้อมใช้งาน'
    : status === 'degraded' ? 'มีข้อควรระวัง'
    : status === 'down' ? 'ติดต่อไม่ได้'
    : status === 'disabled' ? 'ปิดใช้งานชั่วคราว'
    : 'ยังไม่ตั้งค่า';
}

function statusColor(status: ServiceStatusDto['status'] | ProviderStatusDto['availability']): string {
  return status === 'operational' ? 'var(--sv-ok-soft)'
    : status === 'degraded' || status === 'unconfigured' ? 'var(--sv-warn)'
    : status === 'disabled' ? 'var(--sv-neutral-fg)'
    : 'var(--sv-danger)';
}

const PLATFORM_REVENUE_RANGE_OPTIONS: { key: RevenueSeriesRange; label: string }[] = [
  { key: '7d', label: '7 วัน' },
  { key: '14d', label: '14 วัน' },
  { key: '30d', label: '1 เดือน' },
  { key: 'all', label: 'ทั้งหมด' },
];

function PlatformRevenueTrendCard({ token, events }: { token: string; events: TenantSummaryDto[] }) {
  const [range, setRange] = useState<RevenueSeriesRange>('7d');
  const [eventId, setEventId] = useState<string>('');
  const [series, setSeries] = useState<AdminRevenueSeriesPointDto[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    superadminApi.getRevenueSeries(token, range, eventId || undefined)
      .then((data) => { if (!cancelled) { setSeries(data); setError(''); } })
      .catch(() => { if (!cancelled) setError('โหลดแนวโน้มรายได้ไม่สำเร็จ'); });
    return () => { cancelled = true; };
  }, [token, range, eventId]);

  return (
    <div className="admin-group" style={{ marginTop: 16 }}>
      <div className="admin-group-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>แนวโน้มรายได้</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>ดูรวมทุกอีเวนต์ หรือกรองเป็นรายอีเวนต์</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              aria-label="กรองแนวโน้มรายได้ตามอีเวนต์"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              style={{
                padding: '7px 10px', borderRadius: 8, fontSize: 12.5,
                background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)',
              }}
            >
              <option value="">ทุกอีเวนต์</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              {PLATFORM_REVENUE_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className="admin-range-btn"
                  aria-pressed={range === opt.key}
                  onClick={() => setRange(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <RevenueTrendChart series={series} error={error} />
      </div>
    </div>
  );
}

function OverviewPanel({
  overview, events, refreshing, onRefresh, token,
}: {
  overview: PlatformOverviewDto | null;
  events: TenantSummaryDto[];
  refreshing: boolean;
  onRefresh: () => void;
  token: string;
}) {
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState('');

  const handleToggleProvider = async (provider: ProviderStatusDto) => {
    const nextEnabled = provider.availability === 'disabled';
    setToggleError('');
    setTogglingId(provider.id);
    try {
      await superadminApi.toggleProvider(token, provider.id, nextEnabled);
      onRefresh();
    } catch {
      setToggleError(`สลับสถานะ ${provider.name} ไม่สำเร็จ`);
    } finally {
      setTogglingId(null);
    }
  };

  const [pollingIntervalDraft, setPollingIntervalDraft] = useState('60');
  const [pollingSaving, setPollingSaving] = useState(false);
  const [pollingError, setPollingError] = useState('');

  useEffect(() => {
    if (overview) setPollingIntervalDraft(String(overview.adminPolling.intervalSec));
  }, [overview?.adminPolling.intervalSec]);

  const saveAdminPolling = async (next: Partial<{ enabled: boolean; intervalSec: number }>) => {
    if (!overview) return;
    const settings = {
      enabled: next.enabled ?? overview.adminPolling.enabled,
      intervalSec: next.intervalSec ?? overview.adminPolling.intervalSec,
    };
    setPollingError('');
    setPollingSaving(true);
    try {
      await superadminApi.updateAdminPolling(token, settings);
      onRefresh();
    } catch {
      setPollingError('บันทึกการตั้งค่า auto-refresh ไม่สำเร็จ');
    } finally {
      setPollingSaving(false);
    }
  };

  if (!overview) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>กำลังโหลด…</div>;
  const cards = [
    { label: 'อีเวนต์ทั้งหมด', value: String(overview.eventCount), sub: `${events.filter((e) => e.status === 'active').length} กำลังขาย` },
    { label: 'รายได้รวม (ชำระแล้ว)', value: fmt(overview.totalRevenue), sub: 'ทุกอีเวนต์' },
    { label: 'ออร์เดอร์ทั้งหมด', value: String(overview.totalOrders), sub: 'ทุกสถานะ' },
  ];
  const providerAlerts = overview.providers.filter((provider) => provider.availability === 'degraded' || provider.availability === 'down');
  const deadLetterCount = overview.slipVerificationDeadLetterCount ?? 0;
  return (
    <>
      {providerAlerts.length > 0 && (
        <div className="form-error" role="alert" style={{ marginBottom: 16, lineHeight: 1.6 }}>
          <strong>ผู้ให้บริการภายนอกต้องตรวจสอบ:</strong>{' '}
          {providerAlerts.map((provider) => `${provider.name} — ${provider.detail}`).join(' · ')}
        </div>
      )}
      {deadLetterCount > 0 && (
        <div className="form-error" role="alert" style={{ marginBottom: 16, lineHeight: 1.6 }}>
          <strong>มี {deadLetterCount} ออร์เดอร์ที่ auto-retry ตรวจสลิปครบ 3 รอบแล้วยังไม่สำเร็จ</strong> —
          ตรวจสอบเองที่แท็บ "ธุรกรรม &amp; การส่งบัตร"
        </div>
      )}
      <div className="sa-stat-grid">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <PlatformRevenueTrendCard token={token} events={events} />

      <div className="admin-group" style={{ marginTop: 16 }}>
        <div className="admin-group-body">
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 18 }}>
            อีเวนต์ในระบบ
          </div>
          {events.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>ยังไม่มีอีเวนต์</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {events.map((e) => {
                const pct = e.capacity > 0 ? Math.min(100, Math.round((e.sold / e.capacity) * 100)) : 0;
                const days = daysSince(e.createdAt);
                return (
                  <div key={e.id}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, fontSize: 14 }}>
                      <span>{e.name} <span className={`status-chip ${e.status === 'active' ? 'paid' : 'pending_payment'}`} style={{ marginLeft: 8 }}>{STATUS_LABEL[e.status]}</span></span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)' }}>
                        เปิดขายมาแล้ว {days} วัน · {e.orders} ออร์เดอร์ · {fmt(e.revenue)} · ขาย {e.sold}/{e.capacity} · รอชำระ {e.reservedOnline ?? 0} · พิมพ์รอขาย {e.reservedPrinted ?? 0}
                      </span>
                    </div>
                    <svg viewBox="0 0 100 5" preserveAspectRatio="none" style={{ width: '100%', height: 5, marginTop: 9 }}>
                      <rect x="0" y="0" width="100" height="5" rx="2.5" fill="var(--panel-border)" />
                      <rect x="0" y="0" width={pct} height="5" rx="2.5" fill="var(--fg)" />
                    </svg>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="admin-group" style={{ marginTop: 16 }}>
        <div className="admin-group-body">
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>สถานะระบบหลัก</div>
          <SystemPanel services={overview.coreServices} />
        </div>
      </div>

      <div className="admin-group" style={{ marginTop: 16 }}>
        <div className="admin-group-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>ผู้ให้บริการภายนอกและโควต้า</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>สถานะจาก API ของผู้ให้บริการ · cache ไม่เกิน 3 นาที</div>
            </div>
            <button className="btn-ghost" disabled={refreshing} onClick={onRefresh}><IconRefresh /> {refreshing ? 'กำลังตรวจ…' : 'ตรวจสอบใหม่'}</button>
          </div>
          {toggleError && <div className="form-error" role="alert" style={{ marginBottom: 14 }}>{toggleError}</div>}
          {pollingError && <div className="form-error" role="alert" style={{ marginBottom: 14 }}>{pollingError}</div>}
          <div className="sa-stat-grid">
            {overview.providers.map((provider) => {
              const manuallyDisabled = provider.availability === 'disabled';
              const isChannelProvider = provider.id === 'resend' || provider.id === 'thaibulksms';
              const verifyCount = provider.id === 'easyslip' ? overview.slipVerificationStats?.byVendor.easyslip
                : provider.id === 'thunder' ? overview.slipVerificationStats?.byVendor.thunder
                : undefined;
              return (
                <div key={provider.id} className="sa-service-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div><div style={{ fontWeight: 600 }}>{provider.name}</div><div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{provider.purpose}</div></div>
                    <ToggleSwitch
                      checked={!manuallyDisabled}
                      disabled={togglingId === provider.id}
                      onChange={() => handleToggleProvider(provider)}
                      title={manuallyDisabled ? 'เปิดใช้งานผู้ให้บริการนี้' : 'ปิดใช้งานชั่วคราว'}
                    />
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--muted)', marginTop: 10 }}>{provider.detail}</div>
                  {!manuallyDisabled && isChannelProvider && provider.channelEnabled !== undefined && (
                    <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: provider.channelEnabled ? 'var(--sv-ok-soft)' : 'var(--sv-warn)' }}>
                      {provider.channelEnabled ? 'ช่องทางส่งเปิดใช้งาน' : `การส่ง ${provider.id === 'resend' ? 'Email' : 'SMS'} ปิดอยู่ (rollout flag)`}
                    </div>
                  )}
                  {provider.capacity && (
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                      คงเหลือ <strong>{provider.capacity.remaining ?? 'ไม่ทราบ'}</strong>
                      {provider.capacity.limit !== null && <> / {provider.capacity.limit}</>}
                      {' '}{provider.capacity.unit === 'credits' ? 'เครดิต' : 'ครั้ง'}
                    </div>
                  )}
                  {verifyCount !== undefined && (
                    <div style={{ marginTop: 10, fontSize: 13 }}>
                      <VendorBadge vendor={provider.id as 'easyslip' | 'thunder'} /> ผ่าน {verifyCount}
                    </div>
                  )}
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 10 }}>ตรวจล่าสุด {new Date(provider.checkedAt).toLocaleString('th-TH')}</div>
                  <div style={{ marginTop: 12, textAlign: 'center', fontSize: 12, fontWeight: 600, color: statusColor(provider.availability) }}>
                    ● {statusText(provider.availability)}
                  </div>
                </div>
              );
            })}

            <div className="sa-service-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Auto-refresh หน้า Admin</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>รีเฟรชภาพรวม/ตรวจออร์เดอร์อัตโนมัติ · มีผลทุกอีเวนต์</div>
                </div>
                <ToggleSwitch
                  checked={overview.adminPolling.enabled}
                  disabled={pollingSaving}
                  onChange={() => saveAdminPolling({ enabled: !overview.adminPolling.enabled })}
                  title={overview.adminPolling.enabled ? 'ปิด auto-refresh ทุก Event' : 'เปิด auto-refresh ทุก Event'}
                />
              </div>
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                ทุก
                <input
                  type="number"
                  aria-label="ความถี่ auto-refresh (วินาที)"
                  min={15}
                  max={600}
                  value={pollingIntervalDraft}
                  disabled={!overview.adminPolling.enabled || pollingSaving}
                  onChange={(e) => setPollingIntervalDraft(e.target.value)}
                  style={{
                    width: 64, padding: '6px 8px', borderRadius: 8,
                    background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13,
                  }}
                />
                วินาที (15–600)
                <button
                  className="btn-ghost"
                  style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 10px' }}
                  disabled={pollingSaving || !overview.adminPolling.enabled}
                  onClick={() => saveAdminPolling({ intervalSec: Math.min(600, Math.max(15, parseInt(pollingIntervalDraft, 10) || 60)) })}
                >
                  บันทึก
                </button>
              </div>
              <div style={{ marginTop: 12, textAlign: 'center', fontSize: 12, fontWeight: 600, color: overview.adminPolling.enabled ? 'var(--sv-ok-soft)' : 'var(--sv-neutral-fg)' }}>
                ● {overview.adminPolling.enabled ? `เปิดใช้งาน ทุก ${overview.adminPolling.intervalSec} วิ` : 'ปิดอยู่'}
              </div>
            </div>
          </div>
          {overview.slipVerificationStats && (
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', paddingTop: 14, marginTop: 14, borderTop: '1px solid var(--panel-border)', fontSize: 13 }}>
              <span><VendorBadge vendor="manual" /> ตรวจด้วยคน {overview.slipVerificationStats.byVendor.manual}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- events ---------- */
/** One copyable full-URL row (landing / admin / check-in) for an event card. */
function EventUrlRow({ label, path }: { label: string; path: string }) {
  const [copied, setCopied] = useState(false);
  const full = `${window.location.origin}${path}`;

  const copy = () => {
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ minWidth: 60, flex: 'none', fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--mono)' }}>
        {label}
      </div>
      <a
        href={path}
        target="_blank"
        rel="noreferrer"
        title={full}
        style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}
      >
        {full}
      </a>
      <button type="button" className="sa-icon-btn" style={{ width: 24, height: 24, flex: 'none' }} title="คัดลอกลิงก์" onClick={copy}>
        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
      </button>
    </div>
  );
}

/**
 * Lazily fetches and reveals this event's current token for a given role (owner or check-in) —
 * so a superadmin can recover it later without having to have copied it at issue time. Stays
 * hidden behind a reveal button until clicked, same pattern for every role it's used with.
 */
function TokenReveal({ token, eventId, role, label }: { token: string; eventId: string; role: AccessTokenDto['role']; label: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [found, setFound] = useState<AccessTokenDto | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setState('loading');
    try {
      const rows = await superadminApi.listTokens(token, eventId);
      setFound(rows.find((r) => r.role === role && r.active && r.token) ?? null);
      setState('loaded');
    } catch {
      setState('error');
    }
  };

  const copy = () => {
    if (!found?.token) return;
    navigator.clipboard.writeText(found.token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (state === 'idle') {
    return (
      <button type="button" className="btn-ghost" style={{ fontSize: 11.5, padding: '7px 10px' }} onClick={load}>
        แสดง {label}
      </button>
    );
  }
  if (state === 'loading') return <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>กำลังโหลด…</div>;
  if (state === 'error') return <div style={{ fontSize: 11.5, color: 'var(--sv-danger)' }}>โหลด {label} ไม่สำเร็จ</div>;
  if (!found) {
    return <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>ไม่มี {label} ที่ใช้งานได้ — ออกใหม่ได้ที่แท็บ "ผู้ใช้ &amp; Token"</div>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ minWidth: 60, flex: 'none', fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--mono)' }}>
        {label.toLowerCase()}
      </div>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
        {found.token}
      </span>
      <button type="button" className="sa-icon-btn" style={{ width: 24, height: 24, flex: 'none' }} title={`คัดลอก ${label}`} onClick={copy}>
        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
      </button>
    </div>
  );
}

const VENDOR_LABEL_SHORT: Record<'thunder' | 'easyslip', string> = { thunder: 'Thunder', easyslip: 'EasySlip' };

/**
 * TICK-051 — per-event Thunder/EasySlip API keys. Collapsed by default (secrets, not something an
 * events grid should show open by default); opening loads only the masked preview
 * (`getEventVendorKeys`) — the plaintext key is fetched one vendor at a time through the audited
 * reveal, the same click-to-reveal shape `TokenReveal` above already uses for owner tokens.
 */
function VendorKeysSection({ token, eventId }: { token: string; eventId: string }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<EventVendorKeysDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [drafts, setDrafts] = useState<{ thunder: string; easyslip: string }>({ thunder: '', easyslip: '' });
  const [revealed, setRevealed] = useState<{ thunder: string | null; easyslip: string | null }>({ thunder: null, easyslip: null });
  const [saving, setSaving] = useState(false);
  const [checkResult, setCheckResult] = useState<Partial<Record<'easyslip' | 'thunder', { status: string }>> | null>(null);

  const load = async () => {
    setOpen(true);
    if (summary) return;
    setLoading(true);
    try {
      setSummary(await superadminApi.getEventVendorKeys(token, eventId));
      setErr('');
    } catch {
      setErr('โหลดการตั้งค่าไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  const reveal = async (vendor: 'thunder' | 'easyslip') => {
    try {
      const res = await superadminApi.revealEventVendorKey(token, eventId, vendor);
      setRevealed((r) => ({ ...r, [vendor]: res.key }));
    } catch {
      setErr(`เปิดดู ${VENDOR_LABEL_SHORT[vendor]} key ไม่สำเร็จ`);
    }
  };

  const save = async (patch: EventVendorKeysPatchDto) => {
    setSaving(true);
    setErr('');
    try {
      const res = await superadminApi.updateEventVendorKeys(token, eventId, patch);
      setSummary(res.vendorKeys);
      setCheckResult(res.checks);
      setDrafts({ thunder: '', easyslip: '' });
      setRevealed({ thunder: null, easyslip: null });
    } catch {
      setErr('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn-ghost" style={{ fontSize: 11.5, padding: '7px 10px' }} onClick={load}>
        ตั้งค่าผู้ให้บริการตรวจสลิป
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0 2px' }}>
      {loading && <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>กำลังโหลด…</div>}
      {err && <div style={{ fontSize: 11.5, color: 'var(--sv-danger)' }}>{err}</div>}
      {summary && (
        <>
          {(['thunder', 'easyslip'] as const).map((vendor) => {
            const preview = vendor === 'thunder' ? summary.thunderPreview : summary.easySlipPreview;
            return (
              <div key={vendor} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--mono)' }}>
                  {VENDOR_LABEL_SHORT[vendor]} API key
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="text"
                    value={drafts[vendor]}
                    onChange={(e) => setDrafts((d) => ({ ...d, [vendor]: e.target.value }))}
                    placeholder={revealed[vendor] ?? preview ?? 'ยังไม่ได้ตั้งค่า — provider นี้ปิดสำหรับ Event นี้'}
                    style={{
                      flex: 1, minWidth: 0, fontFamily: 'var(--mono)', fontSize: 11.5, padding: '6px 9px',
                      borderRadius: 7, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)',
                    }}
                  />
                  {preview && !revealed[vendor] && (
                    <button type="button" className="sa-icon-btn" style={{ width: 24, height: 24, flex: 'none' }} title="แสดง key เต็ม" onClick={() => reveal(vendor)}>
                      <IconEye size={12} />
                    </button>
                  )}
                  <button
                    type="button" className="btn-ghost" style={{ fontSize: 11, padding: '5px 10px', flex: 'none' }}
                    disabled={saving || !drafts[vendor].trim()}
                    onClick={() => save({ [vendor === 'thunder' ? 'thunderApiKey' : 'easySlipApiKey']: drafts[vendor].trim() })}
                  >
                    บันทึก
                  </button>
                  {preview && (
                    <button
                      type="button" className="sa-icon-btn danger" style={{ width: 24, height: 24, flex: 'none' }} title="ล้าง key (ปิด provider นี้)"
                      disabled={saving}
                      onClick={() => save({ [vendor === 'thunder' ? 'thunderApiKey' : 'easySlipApiKey']: null })}
                    >
                      <IconX size={12} />
                    </button>
                  )}
                </div>
                {checkResult?.[vendor] && (
                  <div style={{ fontSize: 10.5, color: checkResult[vendor]!.status === 'ok' ? 'var(--sv-ok-soft)' : 'var(--sv-warn)' }}>
                    ทดสอบเชื่อมต่อ: {checkResult[vendor]!.status === 'ok' ? 'สำเร็จ' : `ไม่สำเร็จ (${checkResult[vendor]!.status}) — บันทึกไว้แล้ว ลองใหม่ภายหลังได้`}
                  </div>
                )}
              </div>
            );
          })}
          {summary.thunderPreview && summary.easySlipPreview && (
            <label style={{ fontSize: 11.5, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
              ผู้ให้บริการหลัก
              <select
                value={summary.primary}
                onChange={(e) => save({ primary: e.target.value as 'easyslip' | 'thunder' })}
                disabled={saving}
                style={{ fontFamily: 'var(--mono)', fontSize: 11.5, padding: '4px 8px', borderRadius: 6, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)' }}
              >
                <option value="easyslip">EasySlip</option>
                <option value="thunder">Thunder</option>
              </select>
            </label>
          )}
          <div style={{ fontSize: 10.5, color: 'var(--dim)', lineHeight: 1.6 }}>
            Event ที่ไม่มี key ของตัวเอง provider นั้นจะปิดโดยอัตโนมัติ ไม่มีการใช้ key กลางของแพลตฟอร์มแทน
          </div>
        </>
      )}
    </div>
  );
}

function EventsPanel({
  token, events, onOpenNew, onReset, onToggleStatus, onDelete, onSaveTemplate, statusBusy,
}: {
  token: string;
  events: TenantSummaryDto[];
  onOpenNew: () => void;
  onReset: (e: TenantSummaryDto) => void;
  onToggleStatus: (e: TenantSummaryDto) => void;
  onDelete: (e: TenantSummaryDto) => void;
  onSaveTemplate: (e: TenantSummaryDto) => void;
  statusBusy: string | null;
}) {
  return (
    <div className="sa-tenant-grid">
      {events.map((e) => {
        const pct = e.capacity > 0 ? Math.min(100, Math.round((e.sold / e.capacity) * 100)) : 0;
        const prefix = `/e/${e.slug}`;
        return (
          <div key={e.id} className="admin-group sa-tenant-card">
            <div className="sa-tenant-card-body">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{e.name}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--dim)', marginTop: 5 }}>
                    /e/{e.slug} {e.isDefault ? '· default' : ''}
                  </div>
                </div>
                <span className={`status-chip ${e.status === 'active' ? 'paid' : e.status === 'paused' ? 'pending_payment' : 'rejected'}`}>
                  {STATUS_LABEL[e.status]}
                </span>
              </div>
              {e.dateLabel && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 12 }}>{e.dateLabel}</div>}
              {/* TICK-160 — the label above is free text; this is what the ticket links are actually dated from. */}
              {e.ticketLinkBlock && <div className="form-warn" role="note">ส่งตั๋วไม่ได้ — {e.ticketLinkBlock.message}</div>}
            </div>
            <div className="sa-tenant-card-stats">
              <div><div style={{ fontSize: 11, color: 'var(--dim)' }}>ออร์เดอร์</div><div style={{ fontSize: 17, fontWeight: 600, marginTop: 3 }}>{e.orders}</div></div>
              <div><div style={{ fontSize: 11, color: 'var(--dim)' }}>รายได้</div><div style={{ fontSize: 17, fontWeight: 600, marginTop: 3 }}>{fmt(e.revenue)}</div></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--dim)' }}>
                  <span>บัตรที่ขายแล้ว</span><span style={{ fontFamily: 'var(--mono)', color: 'var(--muted)' }}>{e.sold} / {e.capacity} · รอชำระ {e.reservedOnline ?? 0} · พิมพ์รอขาย {e.reservedPrinted ?? 0}</span>
                </div>
                <div style={{ height: 5, background: 'var(--line)', borderRadius: 9999, overflow: 'hidden', marginTop: 7 }}>
                  <div style={{ height: '100%', borderRadius: 9999, background: 'var(--fg)', width: `${pct}%` }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 20px', borderTop: '1px solid var(--panel-border)' }}>
              <EventUrlRow label="Landing" path={prefix} />
              <EventUrlRow label="Admin" path={`${prefix}/admin`} />
              <EventUrlRow label="Check-in" path={`${prefix}/staff/checkin`} />
              <EventUrlRow label="Sales" path={`${prefix}/admin`} />
              <TokenReveal token={token} eventId={e.id} role="owner" label="Owner Token" />
              <VendorKeysSection token={token} eventId={e.id} />
            </div>
            <div className="sa-tenant-card-foot">
              <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>ทีม {e.teamCount} คน</div>
              <button type="button" className="sa-icon-btn" title="บันทึกเป็นเทมเพลต" onClick={() => onSaveTemplate(e)}>
                <IconBookmark />
              </button>
              <button type="button" className="sa-icon-btn" title="รีเซ็ต Transaction" onClick={() => onReset(e)}>
                <IconRefresh />
              </button>
              <button
                type="button"
                className="sa-icon-btn"
                title={e.status === 'active' ? 'หยุดใช้งาน' : 'เปิดใช้งาน'}
                disabled={statusBusy === e.id}
                onClick={() => onToggleStatus(e)}
              >
                {e.status === 'active' ? <IconStop /> : <IconPlay />}
              </button>
              {!e.isDefault && (
                <button type="button" className="sa-icon-btn danger" title="ลบอีเวนต์" onClick={() => onDelete(e)}>
                  <IconTrash />
                </button>
              )}
            </div>
          </div>
        );
      })}
      <button className="sa-new-tenant-btn" onClick={onOpenNew}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconPlus size={22} />
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 500 }}>สร้างอีเวนต์ใหม่</div>
        <div style={{ fontSize: 11.5, color: 'var(--dim)', maxWidth: 200, textAlign: 'center', lineHeight: 1.5 }}>Workspace + หน้าขาย + Owner token</div>
      </button>
    </div>
  );
}

/* ---------- home cms ---------- */
function HomeCmsPanel({
  events,
  token,
  onRefresh,
}: {
  events: TenantSummaryDto[];
  token: string;
  onRefresh: () => Promise<void>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string>('');

  /*
   * TICK-142 — the last two hand-written captions in this file. `Field` cannot be reused here: it
   * names its child with `aria-labelledby` because one of its uses wraps a grid of buttons, and
   * each of these wraps exactly one control, where a real `<label>` is the better answer — it
   * names the control *and* makes the caption a click target for it.
   */
  const orgTitleId = useId();
  const orgSubtitleId = useId();

  // Organizer Section CMS State
  const [orgEnabled, setOrgEnabled] = useState(true);
  const [orgTitle, setOrgTitle] = useState('สร้างระบบขายตั๋วคอนเสิร์ตและเฟสติวัลของคุณเอง');
  const [orgSubtitle, setOrgSubtitle] = useState('แพลตฟอร์มจัดการตั๋วแบบ Multi-tenant แยก Workspace อิสระ รองรับระบบตรวจสอบสลิปอัตโนมัติ และสแกน QR หน้างานเรียลไทม์');
  const [orgCards, setOrgCards] = useState([
    { emoji: '🚀', title: 'Workspace อิสระ', desc: 'รับ URL ประจำงาน `/e/your-event` มีระบบแอดมินและตั๋วจำหน่ายแยกเฉพาะงาน' },
    { emoji: '⚡', title: 'ตรวจสลิปอัตโนมัติ', desc: 'เชื่อมต่อ EasySlip / Thunder Verify ตรวจสอบยอดและสลิปปลอมอัตโนมัติ 24 ชม.' },
    { emoji: '📱', title: 'สแกน QR หน้างาน', desc: 'ทีมงานสแกนบัตรผ่านกล้องสมาร์ตโฟน เช็กอินรวดเร็ว ป้องกันตั๋วซ้ำเรียลไทม์' },
    { emoji: '📊', title: 'Dashboard รายงานผล', desc: 'ติดตามยอดขาย รายรับ โควต้าตั๋ว และส่งออกรายงาน CSV บัญชีได้ทันที' },
  ]);
  const [savingOrg, setSavingOrg] = useState(false);

  useEffect(() => {
    api.getPortalSettings().then((s) => {
      if (s) {
        setOrgEnabled(s.organizerSectionEnabled !== false);
        if (s.organizerSectionTitle) setOrgTitle(s.organizerSectionTitle);
        if (s.organizerSectionSubtitle) setOrgSubtitle(s.organizerSectionSubtitle);
        if (s.organizerCards && s.organizerCards.length > 0) setOrgCards(s.organizerCards);
      }
    }).catch(() => {});
  }, []);

  const saveOrganizerSettings = async () => {
    setSavingOrg(true);
    try {
      await superadminApi.updatePortalSettings(token, {
        organizerSectionEnabled: orgEnabled,
        organizerSectionTitle: orgTitle,
        organizerSectionSubtitle: orgSubtitle,
        organizerCards: orgCards,
      });
      showToast('บันทึกการตั้งค่าส่วน "สำหรับผู้จัดงาน" เรียบร้อยแล้ว');
    } catch {
      alert('บันทึกข้อมูลไม่สำเร็จ');
    } finally {
      setSavingOrg(false);
    }
  };

  const sortedEvents = [...events].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const updateCms = async (
    eventId: string,
    patch: { portalVisible?: boolean; portalTeaser?: boolean; carouselVisible?: boolean; sortOrder?: number },
  ) => {
    setBusyId(eventId);
    try {
      await superadminApi.updateEventPortalCms(token, eventId, patch);
      await onRefresh();
      showToast('บันทึกการตั้งค่าหน้าหลักเรียบร้อย');
    } catch {
      alert('ไม่สามารถอัปเดตข้อมูลได้');
    } finally {
      setBusyId(null);
    }
  };

  const updateSalesMode = async (eventId: string, salesMode: EventSalesMode) => {
    if (salesMode === 'public' && !confirm('เปิดขายให้คนทั่วไปเข้าถึงงานนี้ได้ทันที ยืนยันหรือไม่?')) return;
    setBusyId(eventId);
    try {
      await superadminApi.setEventSalesMode(token, eventId, salesMode);
      await onRefresh();
      showToast(`เปลี่ยนโหมดการขายเป็น "${SALES_MODE_LABEL[salesMode]}" แล้ว`);
    } catch {
      alert('ไม่สามารถเปลี่ยนโหมดการขายได้');
    } finally {
      setBusyId(null);
    }
  };

  const moveOrder = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedEvents.length) return;

    const curr = sortedEvents[index];
    const prevOrNext = sortedEvents[targetIndex];

    const currOrder = curr.sortOrder ?? index;
    const targetOrder = prevOrNext.sortOrder ?? targetIndex;

    const newCurrOrder = targetOrder;
    const newTargetOrder = currOrder === targetOrder ? (direction === 'up' ? targetOrder - 1 : targetOrder + 1) : currOrder;

    setBusyId(curr.id);
    try {
      await superadminApi.updateEventPortalCms(token, curr.id, { sortOrder: newCurrOrder });
      await superadminApi.updateEventPortalCms(token, prevOrNext.id, { sortOrder: newTargetOrder });
      await onRefresh();
      showToast('ปรับลำดับอีเวนต์เรียบร้อย');
    } catch {
      alert('ไม่สามารถเปลี่ยนลำดับได้');
    } finally {
      setBusyId(null);
    }
  };

  const portalVisibleCount = events.filter((e) => e.portalVisible !== false).length;
  const carouselVisibleCount = events.filter((e) => e.carouselVisible !== false).length;

  return (
    <div>
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'var(--sv-ok-soft)',
            color: 'var(--sv-on-accent)',
            padding: '10px 18px',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            zIndex: 100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          ✓ {toast}
        </div>
      )}

      {/* Overview Stat Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="sa-service-card">
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', fontFamily: 'var(--mono)' }}>
            อีเวนต์ทั้งหมดในระบบ
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{events.length} งาน</div>
        </div>
        <div className="sa-service-card">
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', fontFamily: 'var(--mono)' }}>
            แสดงบนหน้า Portal (Home)
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sv-ok-soft)', marginTop: 4 }}>{portalVisibleCount} งาน</div>
        </div>
        <div className="sa-service-card">
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', fontFamily: 'var(--mono)' }}>
            แสดงบน Hero Carousel
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sv-accent)', marginTop: 4 }}>{carouselVisibleCount} งาน</div>
        </div>
      </div>

      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: '70px 2.2fr 1fr 1.4fr 1.2fr 1.3fr 1.3fr auto' }}>
          <div>ลำดับ</div>
          <div>อีเวนต์</div>
          <div>สถานะขาย</div>
          <div>โหมดการขาย</div>
          <div>แสดงบนหน้าหลัก</div>
          <div>Hero Carousel</div>
          <div>ปรับลำดับ</div>
          <div>เปิดดู</div>
        </div>

        {sortedEvents.map((item, idx) => {
          const isPortalVis = item.portalVisible !== false;
          const isCarouselVis = item.carouselVisible !== false;
          // TICK-074 — only a hidden event can be announced, so the control only exists for one.
          const isUat = item.salesMode === 'uat';
          const isTeased = item.portalTeaser === true;
          const salesMode = item.salesMode ?? 'public';
          const busy = busyId === item.id;

          return (
            <div
              key={item.id}
              className="sa-table-row"
              style={{
                gridTemplateColumns: '70px 2.2fr 1fr 1.4fr 1.2fr 1.3fr 1.3fr auto',
                opacity: busy ? 0.6 : 1,
              }}
            >
              <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--dim)' }}>
                #{idx + 1}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    width: 54,
                    height: 38,
                    borderRadius: 6,
                    background: 'var(--line)',
                    overflow: 'hidden',
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.heroImageUrl ? (
                    <img src={item.heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--dim)' }}>NO IMG</span>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>/e/{item.slug}</div>
                </div>
              </div>

              <div>
                <span className={`status-chip ${item.status === 'active' ? 'paid' : item.status === 'paused' ? 'pending_payment' : 'rejected'}`}>
                  {item.status === 'active' ? 'กำลังขาย' : item.status === 'paused' ? 'พักขาย' : 'เก็บถาวร'}
                </span>
              </div>

              {/*
                TICK-042 sales mode. Deliberately next to "แสดงบนหน้าหลัก" so the difference is
                visible at a glance: that switch only hides the listing, this one decides whether
                anyone outside the team can reach the event at all.
              */}
              <div>
                <select
                  disabled={busy}
                  value={salesMode}
                  onChange={(e) => updateSalesMode(item.id, e.target.value as EventSalesMode)}
                  title={SALES_MODE_HINT[salesMode]}
                  style={{
                    fontSize: 12,
                    padding: '6px 8px',
                    borderRadius: 8,
                    fontWeight: 600,
                    background: salesMode === 'public' ? 'rgba(143, 202, 154, 0.15)' : 'rgba(255, 154, 61, 0.15)',
                    color: salesMode === 'public' ? 'var(--sv-ok-soft)' : 'var(--sv-accent)',
                    border: `1px solid ${salesMode === 'public' ? 'rgba(143, 202, 154, 0.3)' : 'rgba(255, 154, 61, 0.3)'}`,
                  }}
                >
                  <option value="public">🌐 ขายปกติ</option>
                  <option value="uat">🔒 UAT (ทีมงานเท่านั้น)</option>
                  <option value="closed">⛔ ปิดการขาย</option>
                </select>
              </div>

              {/* Portal Visible Switch */}
              <div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => updateCms(item.id, { portalVisible: !isPortalVis })}
                  className={`btn-ghost ${isPortalVis ? 'active' : ''}`}
                  style={{
                    fontSize: 12,
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: isPortalVis ? 'rgba(143, 202, 154, 0.15)' : 'rgba(255,255,255,0.05)',
                    color: isPortalVis ? 'var(--sv-ok-soft)' : 'var(--muted)',
                    border: isPortalVis ? '1px solid rgba(143, 202, 154, 0.3)' : '1px solid var(--border)',
                    fontWeight: 600,
                  }}
                >
                  {isPortalVis ? '👁️ แสดง' : '🙈 ซ่อน'}
                </button>
              </div>

              {/*
                * TICK-074 — announce a UAT event without opening it.
                *
                * Shown only for a `uat` event, because the server refuses the flag on any other and
                * a control that returns 409 whenever it is pressed is worse than no control. The
                * wording says what it does to visibility, not that it is a display preference: this
                * is the operator opting one hidden event out of being hidden.
                */}
              {isUat && (
                <div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => updateCms(item.id, { portalTeaser: !isTeased })}
                    className={`btn-ghost ${isTeased ? 'active' : ''}`}
                    title={isTeased
                      ? 'ชื่อและรูปของงานนี้แสดงบนหน้าแรกในหัวข้อ "พบกันเร็ว ๆ นี้" — กดเพื่อหยุดประกาศ'
                      : 'ประกาศชื่อและรูปของงานนี้บนหน้าแรก งานยังเปิดไม่ได้และลิงก์ยังเข้าไม่ได้'}
                    style={{
                      fontSize: 12,
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: isTeased ? 'rgba(120, 160, 220, 0.15)' : 'rgba(255,255,255,0.05)',
                      color: isTeased ? 'var(--sv-info)' : 'var(--muted)',
                      border: isTeased ? '1px solid rgba(120, 160, 220, 0.3)' : '1px solid var(--border)',
                      fontWeight: 600,
                    }}
                  >
                    {isTeased ? '📣 ประกาศแล้ว' : '🤫 ยังไม่ประกาศ'}
                  </button>
                </div>
              )}

              {/*
                * TICK-081 — read-only here on purpose.
                *
                * The organizer owns this switch, in their own admin console, because it publishes
                * copy they wrote. What the platform operator needs is to be able to see which
                * hidden events are answering their URLs, without being able to publish one by
                * accident from a list of display toggles.
                */}
              {isUat && item.teaserPage && (
                <div>
                  <span
                    className="mono-tag"
                    title="หน้า “พบกันเร็ว ๆ นี้” ของงานนี้เปิดอยู่ — ผู้ที่มีลิงก์เห็นหน้าประกาศ (ไม่เห็น Lineup หรือบัตร) · ผู้จัดปิดได้เองจากหน้า Admin"
                    style={{
                      fontSize: 11,
                      padding: '5px 10px',
                      background: 'rgba(255, 154, 61, 0.12)',
                      color: 'var(--sv-accent)',
                      border: '1px solid rgba(255, 154, 61, 0.3)',
                    }}
                  >
                    หน้าประกาศเปิดอยู่
                  </span>
                </div>
              )}

              {/* Carousel Switch */}
              <div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => updateCms(item.id, { carouselVisible: !isCarouselVis })}
                  className={`btn-ghost ${isCarouselVis ? 'active' : ''}`}
                  style={{
                    fontSize: 12,
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: isCarouselVis ? 'rgba(255, 154, 61, 0.15)' : 'rgba(255,255,255,0.05)',
                    color: isCarouselVis ? 'var(--sv-accent)' : 'var(--muted)',
                    border: isCarouselVis ? '1px solid rgba(255, 154, 61, 0.3)' : '1px solid var(--border)',
                    fontWeight: 600,
                  }}
                >
                  {isCarouselVis ? '🎠 บน Carousel' : '🚫 ซ่อนจาก Carousel'}
                </button>
              </div>

              {/* Move Up/Down Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  disabled={busy || idx === 0}
                  onClick={() => moveOrder(idx, 'up')}
                  className="sa-icon-btn"
                  title="ขยับขึ้น"
                  style={{ opacity: idx === 0 ? 0.3 : 1 }}
                >
                  ▲
                </button>
                <button
                  type="button"
                  disabled={busy || idx === sortedEvents.length - 1}
                  onClick={() => moveOrder(idx, 'down')}
                  className="sa-icon-btn"
                  title="ขยับลง"
                  style={{ opacity: idx === sortedEvents.length - 1 ? 0.3 : 1 }}
                >
                  ▼
                </button>
                <input
                  type="number"
                  value={item.sortOrder ?? idx}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) updateCms(item.id, { sortOrder: val });
                  }}
                  style={{
                    width: 44,
                    padding: '4px 6px',
                    borderRadius: 6,
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    color: 'var(--fg)',
                    fontFamily: 'var(--mono)',
                    fontSize: 12,
                    textAlign: 'center',
                  }}
                />
              </div>

              <div>
                <a
                  href={`/e/${item.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '4px 10px', textDecoration: 'none' }}
                >
                  ดูหน้างาน ↗
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* For Organizer Section Editable CMS Card (At the Bottom) */}
      <div className="sa-service-card" style={{ marginTop: 32, border: '1px solid var(--border)', background: 'var(--panel-bg)', borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono-tag" style={{ borderColor: 'var(--sv-accent)', color: 'var(--sv-accent)', fontSize: 10, padding: '3px 8px' }}>
              FOR ORGANIZER SECTION CMS
            </span>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>ตั้งค่าข้อมูลกรอบ "JAMTICK FOR ORGANIZERS" บนหน้าแรก</h3>
          </div>
          <button
            type="button"
            onClick={() => setOrgEnabled(!orgEnabled)}
            className={`btn-ghost ${orgEnabled ? 'active' : ''}`}
            style={{
              fontSize: 12,
              padding: '6px 14px',
              borderRadius: 8,
              background: orgEnabled ? 'rgba(143, 202, 154, 0.15)' : 'rgba(255,255,255,0.05)',
              color: orgEnabled ? 'var(--sv-ok-soft)' : 'var(--muted)',
              border: orgEnabled ? '1px solid rgba(143, 202, 154, 0.3)' : '1px solid var(--border)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {orgEnabled ? '👁️ แสดงส่วนนี้บนหน้าแรก' : '🙈 ซ่อนส่วนนี้จากหน้าแรก'}
          </button>
        </div>

        <div className="sa-field" style={{ marginBottom: 16 }}>
          <label className="sa-field-label" htmlFor={orgTitleId}>หัวข้อหลัก (Title)</label>
          <input
            id={orgTitleId}
            type="text"
            value={orgTitle}
            onChange={(e) => setOrgTitle(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 14 }}
          />
        </div>

        <div className="sa-field" style={{ marginBottom: 24 }}>
          <label className="sa-field-label" htmlFor={orgSubtitleId}>รายละเอียดคำอธิบาย (Subtitle)</label>
          <textarea
            id={orgSubtitleId}
            rows={2}
            value={orgSubtitle}
            onChange={(e) => setOrgSubtitle(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>
            การ์ดจุดเด่นฟีเจอร์หลัก (Feature Cards — เพิ่มได้สูงสุด 6 การ์ด): ({orgCards.length}/6)
          </div>
          {orgCards.length < 6 && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                if (orgCards.length < 6) {
                  setOrgCards([
                    ...orgCards,
                    { emoji: '✨', title: `ฟีเจอร์ที่ ${orgCards.length + 1}`, desc: 'รายละเอียดฟีเจอร์เพิ่มเติม' },
                  ]);
                }
              }}
              style={{ fontSize: 12, padding: '4px 12px', color: 'var(--sv-accent)', borderColor: 'var(--sv-accent)', fontWeight: 600 }}
            >
              + เพิ่มการ์ดใหม่ ({orgCards.length}/6)
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 24 }}>
          {orgCards.map((card, cIdx) => (
            <div key={cIdx} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  value={card.emoji}
                  onChange={(e) => {
                    const next = [...orgCards];
                    next[cIdx].emoji = e.target.value;
                    setOrgCards(next);
                  }}
                  style={{ width: 44, textAlign: 'center', padding: '6px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 16 }}
                />
                <input
                  type="text"
                  value={card.title}
                  onChange={(e) => {
                    const next = [...orgCards];
                    next[cIdx].title = e.target.value;
                    setOrgCards(next);
                  }}
                  placeholder="หัวข้อการ์ด"
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13, fontWeight: 600 }}
                />
                {orgCards.length > 1 && (
                  <button
                    type="button"
                    title="ลบการ์ดนี้"
                    onClick={() => setOrgCards(orgCards.filter((_, idx) => idx !== cIdx))}
                    style={{
                      border: 'none',
                      background: 'rgba(239,68,68,0.15)',
                      color: 'var(--sv-danger-soft)',
                      borderRadius: 6,
                      padding: '4px 8px',
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
              <textarea
                rows={2}
                value={card.desc}
                onChange={(e) => {
                  const next = [...orgCards];
                  next[cIdx].desc = e.target.value;
                  setOrgCards(next);
                }}
                placeholder="คำอธิบายฟีเจอร์"
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: 12, resize: 'none' }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            type="button"
            className="btn-primary"
            onClick={saveOrganizerSettings}
            disabled={savingOrg}
            style={{ padding: '10px 24px', fontSize: 14, fontWeight: 600 }}
          >
            {savingOrg ? 'กำลังบันทึก...' : '💾 บันทึกการตั้งค่าส่วนผู้จัดงาน'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- leads panel ---------- */
function LeadsPanel({ leads }: { leads: OrganizerLeadDto[] }) {
  const [search, setSearch] = useState('');

  const filtered = leads.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.phone.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <div className="sa-audit-filters" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          type="text"
          aria-label="ค้นหา Leads"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาชื่อผู้ติดต่อ หรือเบอร์โทร..."
          style={{
            flex: 1,
            minWidth: 200,
            padding: '9px 12px',
            borderRadius: 8,
            background: 'var(--panel-bg)',
            border: '1px solid var(--border)',
            color: 'var(--fg)',
            fontSize: 13,
          }}
        />
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>
          รวม {filtered.length} รายชื่อ
        </div>
      </div>

      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: '1.4fr 2fr 1.5fr 1.2fr' }}>
          <div>วัน-เวลา ที่ส่งข้อมูล</div>
          <div>ชื่อผู้ติดต่อ / ชื่องาน</div>
          <div>เบอร์โทรศัพท์</div>
          <div>การดำเนินการ</div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>
            ยังไม่มีข้อมูล Lead ผู้จัดงานติดต่อเข้ามา
          </div>
        ) : (
          filtered.map((item) => (
            <div className="sa-table-row" key={item.id} style={{ gridTemplateColumns: '1.4fr 2fr 1.5fr 1.2fr' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
                {new Date(item.createdAt).toLocaleString('th-TH')}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {item.name}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--sv-ok-soft)', fontWeight: 600 }}>
                {item.phone}
              </div>
              <div>
                <a
                  href={`tel:${item.phone}`}
                  className="btn-primary"
                  style={{
                    fontSize: 11,
                    padding: '5px 12px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  📞 โทรติดต่อ
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------- vendor response capture panel (TICK-048) ---------- */

const VENDOR_LABEL: Record<string, string> = {
  thunder: 'Thunder', easyslip: 'EasySlip', thaibulksms: 'ThaiBulkSMS', resend: 'Resend',
};

const CAPTURE_MODE_LABEL: Record<VendorCaptureSettingsDto['mode'], string> = {
  off: 'ไม่เก็บ',
  redacted: 'เก็บแบบซ่อนข้อมูลส่วนบุคคล',
  full: 'เก็บเต็ม',
};

const VENDOR_LOG_COLUMNS = '1.1fr .8fr .9fr .5fr 1fr .8fr .5fr';

/* ---------- LINE OA (platform) ---------- */

const SA_LINE_OA_STATUS: Record<LineOaStatusDto, { label: string; tone: string }> = {
  draft: { label: 'ร่าง', tone: 'var(--muted)' },
  verification_pending: { label: 'รอตรวจสอบ', tone: 'var(--sv-warn-soft)' },
  active: { label: 'ใช้งานอยู่', tone: 'var(--sv-ok)' },
  verification_failed: { label: 'ตรวจสอบไม่ผ่าน', tone: 'var(--sv-danger-soft)' },
  disabled: { label: 'ปิดใช้งาน', tone: 'var(--muted)' },
  credential_invalid: { label: 'credential ใช้ไม่ได้', tone: 'var(--sv-danger-soft)' },
};

const SA_LINE_OA_FIELD: Record<string, string> = {
  displayName: 'ชื่อที่แสดง',
  basicId: 'LINE Basic ID',
  addFriendUrl: 'ลิงก์เพิ่มเพื่อน',
  messagingChannelId: 'Messaging Channel ID',
  loginChannelId: 'LINE Login Channel ID',
  messagingSecret: 'Channel secret',
  messagingAccessToken: 'Channel access token',
  deploymentSecrets: 'secret ฝั่ง deployment',
};

const SA_LINE_OA_CHECK: Record<LineOaCheckDto['id'], string> = {
  identifiers: 'รูปแบบข้อมูล',
  credential: 'credential ใช้งานได้',
  consistency: 'ตรงกับ OA ที่ LINE ตอบ',
  webhook: 'Webhook',
  login_channel: 'LINE Login',
};

const SA_LINE_OA_MARK: Record<LineOaCheckDto['status'], string> = {
  passed: '✓', failed: '✕', skipped: '–', unverified: '?',
};

/**
 * TICK-064 — every LINE OA the platform knows about, in one place.
 *
 * The screen deliberately reads as two different jobs, because they are:
 *
 *  - **JamTick's own OA** is described here but configured in the deployment. Its secrets are not
 *    editable on this screen and the form says so rather than offering fields that would fail —
 *    the credential that carries every ticket does not get a text box in a browser.
 *  - **An organizer's OA** is fully managed here, as a fallback for an organizer who cannot do it
 *    themselves, and rotation is write-only exactly as it is in their own console.
 *
 * Nothing on this screen can display a secret, because nothing the API returns contains one.
 */
/**
 * QA (2026-08-27): `server/src/routes/rollout.ts` (TICK-070) has shipped a DB-backed kill switch
 * for LINE delivery since — everything defaults OFF — but no Superadmin surface ever called it.
 * An operator who turned on the deployment env vars (`LINE_DELIVERY`, `DELIVERY_SELECTION_V2`) had
 * no way to see or flip the second, runtime layer sitting on top of them, so LINE stayed disabled
 * for every event with no visible reason on this screen.
 */
function DeliveryRolloutPanel({ token, events }: { token: string; events: TenantSummaryDto[] }) {
  const [status, setStatus] = useState<DeliveryRolloutStatusDto | null>(null);
  const [selectionV2On, setSelectionV2On] = useState(false);
  const [mode, setMode] = useState<LineRolloutMode>('off');
  const [masterOn, setMasterOn] = useState(false);
  const [portalOn, setPortalOn] = useState(false);
  const [canaryIds, setCanaryIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await superadminRolloutApi.get(token);
      setStatus(result);
      setSelectionV2On(result.runtime.deliverySelectionV2Enabled);
      setMode(result.runtime.lineRolloutMode);
      setMasterOn(result.runtime.lineDeliveryEnabled);
      setPortalOn(result.runtime.myTicketPortalEnabled);
      setCanaryIds(result.runtime.lineCanaryEventIds);
      setErr('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'โหลดสถานะ rollout ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const dirty = !!status && (
    selectionV2On !== status.runtime.deliverySelectionV2Enabled
    || mode !== status.runtime.lineRolloutMode
    || masterOn !== status.runtime.lineDeliveryEnabled
    || portalOn !== status.runtime.myTicketPortalEnabled
    || canaryIds.length !== status.runtime.lineCanaryEventIds.length
    || canaryIds.some((id) => !status.runtime.lineCanaryEventIds.includes(id))
  );

  const save = async () => {
    setBusy(true);
    setErr('');
    setSaved(false);
    try {
      const { runtime, effective } = await superadminRolloutApi.patch(token, {
        deliverySelectionV2Enabled: selectionV2On,
        lineDeliveryEnabled: masterOn,
        lineRolloutMode: mode,
        lineCanaryEventIds: canaryIds,
        myTicketPortalEnabled: portalOn,
      });
      setStatus((current) => (current ? { ...current, runtime, effective } : current));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึก rollout ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const toggleCanary = (id: string) => {
    setCanaryIds((current) => (current.includes(id) ? current.filter((c) => c !== id) : [...current, id]));
  };

  if (loading) return <div className="sa-card" style={{ padding: 16, marginBottom: 16, fontSize: 13, color: 'var(--muted)' }}>กำลังโหลดสถานะ rollout…</div>;

  return (
    <div className="sa-card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>ช่องทางรับตั๋ว (LINE)</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 12 }}>
        สวิตช์สองชั้น — env var (<code>LINE_DELIVERY</code>, <code>DELIVERY_SELECTION_V2</code>) เปิดที่ deployment เท่านั้น
        แต่ยังต้องเปิดชั้นนี้ด้วย ลูกค้าถึงจะเลือกช่องทาง LINE ได้จริง ค่าเริ่มต้นคือปิดทุกอย่างโดยตั้งใจ
        {' '}ส่วน <code>/myticket</code> เป็นสวิตช์อิสระ ไม่ขึ้นกับสองอันนี้เลย
      </div>

      {status && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14, fontSize: 12.5 }}>
          <div>
            <span style={{ color: 'var(--muted)' }}>Deployment: </span>
            <strong style={{ color: status.deployment.lineDelivery ? 'var(--sv-ok-dim)' : 'var(--sv-danger-dim)' }}>
              {status.deployment.lineDelivery ? 'เปิด' : 'ปิด'}
            </strong>
          </div>
          <div>
            <span style={{ color: 'var(--muted)' }}>ผลลัพธ์จริงตอนนี้: </span>
            <strong style={{ color: status.effective.lineDelivery ? 'var(--sv-ok-dim)' : 'var(--sv-danger-dim)' }}>
              {status.effective.lineDelivery ? 'เปิด' : `ปิด${status.effective.lineBlockedBy ? ` (blocked by: ${status.effective.lineBlockedBy})` : ''}`}
            </strong>
          </div>
        </div>
      )}

      {err && <div className="sa-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          className={selectionV2On ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '6px 14px' }}
          onClick={() => setSelectionV2On((v) => !v)}
        >
          Delivery selection V2: {selectionV2On ? 'เปิด' : 'ปิด'}
        </button>
        <button
          className={masterOn ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '6px 14px' }}
          onClick={() => setMasterOn((v) => !v)}
        >
          LINE master switch: {masterOn ? 'เปิด' : 'ปิด'}
        </button>
        {/*
          TICK-093 — the only lever over `/myticket`, and it belongs on this screen rather than in
          an organizer's console: a buyer's ticket list spans events, so no single organizer owns
          the decision. While it is off every route under it answers 404, and ticket emails leave
          the address out entirely rather than teaching one that does not work.
        */}
        <button
          className={portalOn ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '6px 14px' }}
          onClick={() => setPortalOn((v) => !v)}
        >
          /myticket portal: {portalOn ? 'เปิด' : 'ปิด'}
        </button>
      </div>

      {portalOn && !status?.runtime.myTicketPortalEnabled && (
        <div style={{ fontSize: 12, color: 'var(--sv-warn)', lineHeight: 1.65, marginBottom: 12, padding: '9px 11px', border: '1px dashed var(--sv-wash-warn)', borderRadius: 5 }}>
          เปิดแล้วผู้ซื้อจะเข้า <code>/myticket</code> แล้วเห็นตั๋วที่ชำระเงินแล้วทุกใบที่ผูกกับเบอร์ของตัวเอง
          — ตรวจก่อนว่าไม่มีออเดอร์ทดสอบที่กรอกเบอร์ของคนอื่นไว้
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['off', 'canary', 'all'] as const).map((m) => (
          <button
            key={m}
            className={mode === m ? 'btn-primary' : 'btn-ghost'}
            style={{ fontSize: 12, padding: '6px 14px' }}
            onClick={() => setMode(m)}
          >
            {m === 'off' ? 'ปิดทั้งหมด' : m === 'canary' ? 'เฉพาะ event ที่เลือก (canary)' : 'เปิดทุก event'}
          </button>
        ))}
      </div>

      {mode === 'canary' && (
        <div style={{ display: 'grid', gap: 6, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
          {events.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>ยังไม่มี event</span>}
          {events.map((e) => (
            <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <input type="checkbox" checked={canaryIds.includes(e.id)} onChange={() => toggleCanary(e.id)} />
              {e.name}
            </label>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn-primary" style={{ fontSize: 12, padding: '7px 16px' }} disabled={busy || !dirty} onClick={save}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
        {saved && <span style={{ fontSize: 12, color: 'var(--sv-ok-dim)' }}>บันทึกแล้ว ✓</span>}
      </div>
    </div>
  );
}

function LineOaPanel({ token, events }: { token: string; events: TenantSummaryDto[] }) {
  const [items, setItems] = useState<LineOaConnectionDto[]>([]);
  const [environment, setEnvironment] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [report, setReport] = useState<Record<string, LineOaVerifyDto['report']>>({});
  const [audit, setAudit] = useState<Record<string, LineOaAuditEntryDto[]>>({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    scope: 'platform' as 'platform' | 'event',
    ownerId: '',
    displayName: '',
    basicId: '',
    messagingChannelId: '',
    loginChannelId: '',
    liffId: '',
    addFriendUrl: '',
    capability: 'messaging' as 'messaging' | 'add_friend_only',
  });
  const [secrets, setSecrets] = useState({ messagingChannelSecret: '', messagingAccessToken: '', loginChannelSecret: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, env] = await Promise.all([
        superadminLineOaApi.list(token),
        superadminLineOaApi.environment(token),
      ]);
      setItems(list);
      setEnvironment(env.environment);
      setErr('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'โหลด LINE OA ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  const eventName = (id: string | null) => (id ? events.find((e) => e.id === id)?.name ?? id : '');
  const scopeLabel = (row: LineOaConnectionDto) =>
    (row.ownerScope === 'platform' ? 'JamTick OA' : `ผู้จัด · ${eventName(row.ownerId)}`);

  const create = () => run(async () => {
    await superadminLineOaApi.create(token, {
      ownerScope: draft.scope,
      ownerId: draft.scope === 'platform' ? null : draft.ownerId,
      capability: draft.scope === 'platform' ? 'messaging' : draft.capability,
      displayName: draft.displayName,
      basicId: draft.basicId || null,
      messagingChannelId: draft.messagingChannelId || null,
      loginChannelId: draft.loginChannelId || null,
      liffId: draft.liffId || null,
      addFriendUrl: draft.addFriendUrl || null,
    });
    setCreating(false);
    setDraft((d) => ({ ...d, displayName: '', basicId: '', messagingChannelId: '', loginChannelId: '', liffId: '', addFriendUrl: '' }));
  }, 'สร้าง LINE OA ไม่สำเร็จ');

  const verify = async (row: LineOaConnectionDto) => {
    setBusy(true);
    setErr('');
    try {
      const result = await superadminLineOaApi.verify(token, row.id);
      setReport((r) => ({ ...r, [row.id]: result.report }));
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'ตรวจสอบการเชื่อมต่อไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const openAudit = async (row: LineOaConnectionDto) => {
    if (audit[row.id]) {
      setAudit((a) => { const next = { ...a }; delete next[row.id]; return next; });
      return;
    }
    try {
      const entries = await superadminLineOaApi.audit(token, row.id);
      setAudit((a) => ({ ...a, [row.id]: entries }));
    } catch {
      setErr('โหลดประวัติไม่สำเร็จ');
    }
  };

  const eventsWithoutOa = events.filter((e) => !items.some((i) => i.ownerId === e.id));

  return (
    <div>
      <div className="sa-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>LINE OA ทั้งหมดใน environment นี้</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.7 }}>
          environment: <strong>{environment || '—'}</strong> — ทุกแถวที่นี่ผูกกับ environment นี้เท่านั้น
          และระบบจะปฏิเสธ configuration ที่ถูกคัดลอกข้าม environment มา
          <br />
          secret ของ JamTick OA อยู่ที่ deployment configuration ไม่ได้อยู่บนหน้าจอนี้ — เปลี่ยนได้ที่ตัว deploy
          ส่วน OA ของผู้จัดเก็บแบบเข้ารหัสและ <strong>อ่านกลับไม่ได้</strong> เปลี่ยนได้อย่างเดียวคือใส่ค่าใหม่ทับ
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => setCreating((v) => !v)}>
            {creating ? 'ยกเลิก' : '+ เพิ่ม LINE OA'}
          </button>
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }} onClick={load} disabled={busy}>
            รีเฟรช
          </button>
        </div>
      </div>

      {err && <div className="sa-error" style={{ marginBottom: 12 }}>{err}</div>}

      {creating && (
        <div className="sa-card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>เพิ่ม LINE OA</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {(['platform', 'event'] as const).map((scope) => (
              <button
                key={scope}
                className={draft.scope === scope ? 'btn-primary' : 'btn-ghost'}
                style={{ fontSize: 12, padding: '6px 14px' }}
                onClick={() => setDraft((d) => ({ ...d, scope }))}
              >
                {scope === 'platform' ? 'JamTick OA (แพลตฟอร์ม)' : 'OA ของผู้จัด'}
              </button>
            ))}
          </div>

          {draft.scope === 'event' && (
            <select
              aria-label="เลือกอีเวนต์"
              value={draft.ownerId}
              onChange={(e) => setDraft((d) => ({ ...d, ownerId: e.target.value }))}
              style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13, marginBottom: 12 }}
            >
              <option value="">— เลือกอีเวนต์ —</option>
              {eventsWithoutOa.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <SaField label="ชื่อที่แสดง" value={draft.displayName} onChange={(v) => setDraft((d) => ({ ...d, displayName: v }))} placeholder="เช่น JamTick OA" />
            <SaField label="LINE Basic ID" value={draft.basicId} onChange={(v) => setDraft((d) => ({ ...d, basicId: v }))} placeholder="@jamtick" />
            <SaField label="Messaging API Channel ID" value={draft.messagingChannelId} onChange={(v) => setDraft((d) => ({ ...d, messagingChannelId: v }))} />
            {draft.scope === 'platform' && (
              <SaField label="LINE Login Channel ID" value={draft.loginChannelId} onChange={(v) => setDraft((d) => ({ ...d, loginChannelId: v }))} />
            )}
            <SaField label="LIFF ID (ถ้ามี)" value={draft.liffId} onChange={(v) => setDraft((d) => ({ ...d, liffId: v }))} />
            <SaField label="ลิงก์เพิ่มเพื่อน" value={draft.addFriendUrl} onChange={(v) => setDraft((d) => ({ ...d, addFriendUrl: v }))} placeholder="https://lin.ee/xxxxxxx" />
          </div>

          <button
            className="btn-primary"
            style={{ marginTop: 12, fontSize: 12, padding: '7px 16px' }}
            disabled={busy || !draft.displayName || (draft.scope === 'event' && !draft.ownerId)}
            onClick={create}
          >
            บันทึก
          </button>
        </div>
      )}

      {loading && <div style={{ fontSize: 13, color: 'var(--muted)' }}>กำลังโหลด…</div>}
      {!loading && items.length === 0 && (
        <div className="sa-card" style={{ padding: 16, fontSize: 13, color: 'var(--muted)' }}>
          ยังไม่มี LINE OA ใน environment นี้
        </div>
      )}

      {items.map((row) => {
        const open = openId === row.id;
        const status = SA_LINE_OA_STATUS[row.status];
        return (
          <div key={row.id} className="sa-card" style={{ padding: 16, marginBottom: 12 }}>
            <div
              style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', cursor: 'pointer' }}
              onClick={() => setOpenId(open ? null : row.id)}
            >
              <strong style={{ fontSize: 13.5 }}>{row.displayName}</strong>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{scopeLabel(row)}</span>
              <span style={{ fontSize: 12, color: status.tone }}>● {status.label}</span>
              {row.basicId && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{row.basicId}</span>}
              <span style={{ fontSize: 11.5, color: 'var(--muted)', marginLeft: 'auto' }}>
                {row.lastVerifiedAt ? `ตรวจล่าสุด ${new Date(row.lastVerifiedAt).toLocaleString('th-TH')}` : 'ยังไม่เคยตรวจ'}
              </span>
            </div>

            {open && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                {row.missingFields.length > 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--sv-warn-soft)', marginBottom: 10 }}>
                    ยังไม่ครบ: {row.missingFields.map((f) => SA_LINE_OA_FIELD[f] ?? f).join(', ')}
                  </div>
                )}
                {row.lastVerificationError && (
                  <div className="sa-error" style={{ marginBottom: 10 }}>{row.lastVerificationError}</div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, fontSize: 12.5, color: 'var(--muted)' }}>
                  <div>Messaging Channel ID: {row.messagingChannelId ?? '—'}</div>
                  <div>Login Channel ID: {row.loginChannelId ?? '—'}</div>
                  <div>LIFF: {row.liffId ?? '—'}</div>
                  <div>ลิงก์เพิ่มเพื่อน: {row.addFriendUrl ?? '—'}</div>
                  <div>bot: {row.botUserId ? `${row.botUserId.slice(0, 12)}…` : '—'}</div>
                  <div>configuration version: {row.configVersion}</div>
                </div>

                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 10 }}>
                  secret: {row.secretSource === 'deployment'
                    ? 'อยู่ที่ deployment configuration — แก้ที่หน้าจอนี้ไม่ได้'
                    : `channel secret ${row.secretsConfigured.messagingSecret ? 'ตั้งแล้ว' : 'ยังไม่ตั้ง'}`
                      + ` · access token ${row.secretsConfigured.messagingAccessToken ? 'ตั้งแล้ว' : 'ยังไม่ตั้ง'}`}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} disabled={busy} onClick={() => verify(row)}>
                    ตรวจสอบการเชื่อมต่อ
                  </button>
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 12, padding: '6px 14px' }}
                    disabled={busy}
                    onClick={() => run(() => superadminLineOaApi.setEnabled(token, row.id, row.status === 'disabled'), 'เปลี่ยนสถานะไม่สำเร็จ')}
                  >
                    {row.status === 'disabled' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                  </button>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => openAudit(row)}>
                    {audit[row.id] ? 'ซ่อนประวัติ' : 'ประวัติการแก้ไข'}
                  </button>
                </div>

                {report[row.id] && (
                  <div style={{ marginTop: 12, fontSize: 12.5 }}>
                    {report[row.id].checks.map((c) => (
                      <div key={c.id} style={{ marginTop: 5 }}>
                        <span style={{ color: c.status === 'failed' ? 'var(--sv-danger-soft)' : 'var(--muted)' }}>{SA_LINE_OA_MARK[c.status]}</span>{' '}
                        {SA_LINE_OA_CHECK[c.id]}
                        {c.detail && <span style={{ color: 'var(--muted)' }}> — {c.detail}</span>}
                      </div>
                    ))}
                    <div style={{ color: 'var(--muted)', marginTop: 8 }}>
                      “–” คือข้ามเพราะไม่เกี่ยวกับการตั้งค่าแบบนี้ “?” คือยังตรวจอัตโนมัติไม่ได้ — ทั้งคู่ไม่ใช่ข้อผิดพลาด
                    </div>
                  </div>
                )}

                {/* An add-friend-only connection has no credential to rotate — offering the fields
                    would suggest it needs one, which is the confusion migration 0031 exists to avoid. */}
                {row.secretSource === 'database' && row.capability === 'messaging' && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>เปลี่ยน secret (ใส่ค่าใหม่ทับเท่านั้น)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      <SaField label="Channel secret ใหม่" value={secrets.messagingChannelSecret} password onChange={(v) => setSecrets((s) => ({ ...s, messagingChannelSecret: v }))} />
                      <SaField label="Channel access token ใหม่" value={secrets.messagingAccessToken} password onChange={(v) => setSecrets((s) => ({ ...s, messagingAccessToken: v }))} />
                    </div>
                    <button
                      className="btn-primary"
                      style={{ marginTop: 10, fontSize: 12, padding: '6px 14px' }}
                      disabled={busy || (!secrets.messagingChannelSecret && !secrets.messagingAccessToken)}
                      onClick={() => run(async () => {
                        await superadminLineOaApi.rotateSecrets(token, row.id, {
                          ...(secrets.messagingChannelSecret ? { messagingChannelSecret: secrets.messagingChannelSecret } : {}),
                          ...(secrets.messagingAccessToken ? { messagingAccessToken: secrets.messagingAccessToken } : {}),
                        });
                        setSecrets({ messagingChannelSecret: '', messagingAccessToken: '', loginChannelSecret: '' });
                      }, 'บันทึก secret ไม่สำเร็จ')}
                    >
                      บันทึก secret
                    </button>
                  </div>
                )}

                {audit[row.id] && (
                  <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--muted)' }}>
                    {audit[row.id].length === 0 && <div>ยังไม่มีประวัติ</div>}
                    {audit[row.id].map((entry) => (
                      <div key={entry.id} style={{ marginTop: 5 }}>
                        {new Date(entry.createdAt).toLocaleString('th-TH')} · {entry.action.replace('line_oa.', '')} · {entry.actor}
                        {entry.detail && <span> · {entry.detail.replace(/^connection=[^|]+\| /, '')}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** One labelled input, so the two grids above stay readable. */
function SaField({ label, value, onChange, placeholder, password }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; password?: boolean;
}) {
  return (
    <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
      {label}
      <input
        type={password ? 'password' : 'text'}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          display: 'block', width: '100%', marginTop: 5, padding: '8px 10px', borderRadius: 8,
          background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13,
        }}
      />
    </label>
  );
}

function VendorLogsPanel({ token }: { token: string }) {
  const [items, setItems] = useState<VendorExchangeDto[]>([]);
  const [capture, setCapture] = useState<VendorCaptureSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openBody, setOpenBody] = useState<{ body: string | null; captureMode: string; bodyTruncated: boolean } | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    superadminApi.listVendorExchanges(token)
      .then((res) => { setItems(res.items); setCapture(res.capture); setErr(''); })
      .catch(() => setErr('โหลดข้อมูลการตอบกลับจากผู้ให้บริการไม่สำเร็จ'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (mode: VendorCaptureSettingsDto['mode']) => {
    if (mode === 'full' && !window.confirm(
      'โหมดเก็บเต็มจะบันทึกคำตอบดิบจากผู้ให้บริการ ซึ่งอาจมีชื่อและเลขบัญชีจริงของลูกค้า\n'
      + 'ระบบจะปิดโหมดนี้ให้อัตโนมัติภายใน 48 ชั่วโมง และลบข้อมูลที่เก็บไว้เมื่อครบกำหนด\n\nยืนยันเปิดโหมดเก็บเต็ม?',
    )) return;
    try {
      const res = await superadminApi.setVendorCapture(token, mode);
      setCapture(res.capture);
    } catch {
      setErr('เปลี่ยนโหมดการเก็บไม่สำเร็จ');
    }
  };

  const openRow = async (row: VendorExchangeDto) => {
    if (openId === row.id) { setOpenId(null); setOpenBody(null); return; }
    setOpenId(row.id);
    setOpenBody(null);
    setBodyLoading(true);
    try {
      setOpenBody(await superadminApi.revealVendorExchange(token, row.id));
    } catch {
      setErr('เปิดดูคำตอบไม่สำเร็จ');
      setOpenId(null);
    } finally {
      setBodyLoading(false);
    }
  };

  const filtered = items.filter((item) => {
    if (vendorFilter !== 'all' && item.vendor !== vendorFilter) return false;
    if (errorsOnly && item.httpStatus !== null && item.httpStatus >= 200 && item.httpStatus < 300) return false;
    return true;
  });

  return (
    <div>
      <div className="sa-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>โหมดการเก็บคำตอบ</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 12 }}>
          ค่าเริ่มต้นคือ <strong>เก็บแบบซ่อนข้อมูลส่วนบุคคล</strong> — เก็บโครงสร้างคำตอบ สถานะ และข้อความ error ไว้ครบ
          แต่แทนที่ชื่อ เลขบัญชี และเลขอ้างอิงด้วยเครื่องหมายปิดบัง จึงไม่มีข้อมูลส่วนบุคคลใหม่ถูกบันทึกลงฐานข้อมูล
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {(['off', 'redacted', 'full'] as const).map((mode) => (
            <button
              key={mode}
              className={capture?.mode === mode ? 'btn-primary' : 'btn-ghost'}
              style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={() => changeMode(mode)}
            >
              {CAPTURE_MODE_LABEL[mode]}
            </button>
          ))}
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px', marginLeft: 'auto' }} onClick={load}>
            <IconRefresh /> รีเฟรช
          </button>
        </div>
        {capture?.mode === 'full' && capture.fullModeUntil && (
          <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--sv-wash-warn-edge)', background: 'var(--sv-wash-warn-deep)', fontSize: 12.5, color: 'var(--sv-warn-soft)' }}>
            ⚠️ โหมดเก็บเต็มเปิดอยู่ — จะปิดอัตโนมัติเมื่อ {new Date(capture.fullModeUntil).toLocaleString('th-TH')}
            {' '}และข้อมูลที่เก็บในโหมดนี้จะถูกลบเมื่อครบกำหนดเดียวกัน
          </div>
        )}
      </div>

      {err && <div className="sa-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          aria-label="กรองตามผู้ให้บริการ"
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        >
          <option value="all">ผู้ให้บริการทั้งหมด</option>
          {Object.entries(VENDOR_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--muted)' }}>
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
          แสดงเฉพาะรายการที่ผิดพลาด
        </label>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>
          {filtered.length} รายการ
        </div>
      </div>

      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: VENDOR_LOG_COLUMNS }}>
          <div>เวลา</div>
          <div>ผู้ให้บริการ</div>
          <div>การเรียกใช้</div>
          <div>HTTP</div>
          <div>ผลลัพธ์</div>
          <div>Order</div>
          <div></div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>กำลังโหลด…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>
            {capture?.mode === 'off'
              ? 'การเก็บคำตอบปิดอยู่ — เปิดโหมดใดโหมดหนึ่งด้านบนเพื่อเริ่มบันทึก'
              : 'ยังไม่มีการเรียกผู้ให้บริการที่ถูกบันทึกไว้'}
          </div>
        ) : (
          filtered.map((item) => {
            const failed = item.httpStatus === null || item.httpStatus >= 300;
            return (
              <div key={item.id}>
                <div className="sa-table-row" style={{ gridTemplateColumns: VENDOR_LOG_COLUMNS }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
                    {new Date(item.createdAt).toLocaleString('th-TH')}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{VENDOR_LABEL[item.vendor] ?? item.vendor}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{item.operation}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: failed ? 'var(--sv-danger-soft)' : 'var(--sv-ok-soft)' }}>
                    {item.httpStatus ?? '—'}
                  </div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: failed ? 'var(--sv-danger-soft)' : 'var(--fg)', wordBreak: 'break-word' }}>
                    {item.outcome}
                  </div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{item.orderCode ?? '—'}</div>
                  <div>
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: '5px 11px' }}
                      disabled={!item.hasBody}
                      aria-expanded={openId === item.id}
                      onClick={() => openRow(item)}
                    >
                      {openId === item.id ? 'ปิด' : 'ดูคำตอบ'}
                    </button>
                  </div>
                </div>
                {openId === item.id && (
                  <div style={{ padding: '4px 16px 18px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', padding: '3px 8px', borderRadius: 5,
                        border: `1px solid ${item.captureMode === 'full' ? 'var(--sv-wash-warn-edge)' : 'var(--border)'}`,
                        color: item.captureMode === 'full' ? 'var(--sv-warn-soft)' : 'var(--muted)',
                      }}>
                        {item.captureMode === 'full' ? 'เก็บเต็ม (มีข้อมูลส่วนบุคคล)' : 'ซ่อนข้อมูลส่วนบุคคลแล้ว'}
                      </span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--dim)' }}>
                        {item.requestPath} · {item.durationMs} ms · เก็บถึง {new Date(item.expiresAt).toLocaleString('th-TH')}
                      </span>
                      {item.bodyTruncated && (
                        <span style={{ fontSize: 11.5, color: 'var(--sv-warn-soft)' }}>⚠️ คำตอบยาวเกินขนาดที่เก็บได้ ถูกตัดปลายทิ้ง</span>
                      )}
                      {openBody?.body && (
                        <button
                          className="btn-ghost"
                          style={{ fontSize: 11, padding: '4px 10px', marginLeft: 'auto' }}
                          onClick={() => navigator.clipboard.writeText(openBody.body ?? '')}
                        >
                          <IconCopy /> คัดลอก
                        </button>
                      )}
                    </div>
                    <pre style={{
                      margin: 0, padding: 14, borderRadius: 10, background: 'var(--panel-bg)', border: '1px solid var(--border)',
                      fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.65, color: 'var(--fg)',
                      maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {bodyLoading ? 'กำลังโหลด…' : openBody?.body ?? 'ไม่มีข้อมูลคำตอบ'}
                    </pre>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ---------- calendar panel ---------- */
function CalendarPanel({ token }: { token: string }) {
  const [items, setItems] = useState<ConcertCalendarDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [view, setView] = useState<'table' | 'calendar'>('table');

  // Form states
  const [dateLabel, setDateLabel] = useState('');
  const [eventName, setEventName] = useState('');
  const [artist, setArtist] = useState('');
  const [venue, setVenue] = useState('');
  const [province, setProvince] = useState('กรุงเทพมหานคร');
  const [monthKey, setMonthKey] = useState('2026-08');
  const [sortDate, setSortDate] = useState('2026-08-01');
  const [submitting, setSubmitting] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importState, setImportState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [importErr, setImportErr] = useState('');
  const [fileImporting, setFileImporting] = useState(false);
  const [fileImportErr, setFileImportErr] = useState('');
  const [fileImportResult, setFileImportResult] = useState<ConcertCalendarImportResultDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await superadminApi.listConcertCalendar(token);
      setItems(data);
      setErr('');
    } catch {
      setErr('ไม่สามารถโหลดปฏิทินคอนเสิร์ตได้');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateLabel || !eventName || !artist || !venue || !province) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    setSubmitting(true);
    try {
      await superadminApi.createConcertCalendarEntry(token, {
        dateLabel,
        eventName,
        artist,
        venue,
        province,
        monthKey,
        sortDate,
      });
      setShowAddModal(false);
      setDateLabel('');
      setEventName('');
      setArtist('');
      setVenue('');
      fetchList();
    } catch {
      alert('ไม่สามารถเพิ่มคอนเสิร์ตได้');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImportState('loading');
    setImportErr('');
    try {
      const { draft } = await superadminApi.importConcertFromUrl(token, importUrl.trim());
      if (draft.eventName) setEventName(draft.eventName);
      if (draft.artist) setArtist(draft.artist);
      if (draft.venue) setVenue(draft.venue);
      if (draft.dateLabel) setDateLabel(draft.dateLabel);
      setImportState('idle');
    } catch (e) {
      setImportErr(e instanceof ApiError ? e.message : 'ดึงข้อมูลจากลิงก์ไม่สำเร็จ');
      setImportState('error');
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileImporting(true);
    setFileImportErr('');
    setFileImportResult(null);
    try {
      const result = await superadminApi.importConcertCalendarFile(token, file);
      setFileImportResult(result);
      fetchList();
    } catch (err) {
      setFileImportErr(err instanceof ApiError ? err.message : 'นำเข้าไฟล์ไม่สำเร็จ');
    } finally {
      setFileImporting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`ยืนยันลบคอนเสิร์ต "${name}" ออกจากปฏิทิน?`)) return;
    try {
      await superadminApi.deleteConcertCalendarEntry(token, id);
      fetchList();
    } catch {
      alert('ไม่สามารถลบคอนเสิร์ตได้');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.eventName.toLowerCase().includes(search.toLowerCase()) ||
      i.artist.toLowerCase().includes(search.toLowerCase()) ||
      i.venue.toLowerCase().includes(search.toLowerCase()) ||
      i.province.toLowerCase().includes(search.toLowerCase()),
  );

  const calendarFetchData = useCallback(async () => items, [items]);

  return (
    <div>
      <div style={{ display: 'flex', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 3, marginBottom: 16, width: 'fit-content' }}>
        <button
          type="button"
          onClick={() => setView('table')}
          style={{
            padding: '7px 16px', borderRadius: 4, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: view === 'table' ? 'var(--sv-accent)' : 'transparent', color: view === 'table' ? 'var(--sv-on-accent)' : 'var(--muted)',
          }}
        >
          ตาราง (จัดการ)
        </button>
        <button
          type="button"
          onClick={() => setView('calendar')}
          style={{
            padding: '7px 16px', borderRadius: 4, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: view === 'calendar' ? 'var(--sv-accent)' : 'transparent', color: view === 'calendar' ? 'var(--sv-on-accent)' : 'var(--muted)',
          }}
        >
          ปฏิทิน (มุมมอง)
        </button>
      </div>

      {view === 'calendar' ? (
        <ConcertCalendar embedded fetchData={calendarFetchData} />
      ) : (
      <>
      <div className="sa-audit-filters" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาชื่องาน, ศิลปิน, สถานที่ หรือจังหวัด..."
          style={{
            flex: 1,
            maxWidth: 360,
            padding: '9px 12px',
            borderRadius: 8,
            background: 'var(--panel-bg)',
            border: '1px solid var(--border)',
            color: 'var(--fg)',
            fontSize: 13,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
            รวม {filtered.length} รายการ
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={handleFileImport}
          />
          <button
            type="button"
            className="btn-ghost"
            disabled={fileImporting}
            onClick={() => fileInputRef.current?.click()}
            style={{ fontSize: 13, padding: '8px 16px' }}
            title="นำเข้าจากไฟล์ .csv หรือ .xlsx (ต้องมีคอลัมน์ ชื่องาน/ศิลปิน/สถานที่/วันที่)"
          >
            {fileImporting ? 'กำลังนำเข้า...' : '📄 นำเข้าไฟล์ Excel/CSV'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            style={{ fontSize: 13, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconPlus size={14} /> เพิ่มงานคอนเสิร์ต
          </button>
        </div>
      </div>

      {fileImportErr && <div className="form-error">{fileImportErr}</div>}

      {fileImportResult && (
        <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              ผลการนำเข้าไฟล์: นำเข้าแล้ว {fileImportResult.summary.imported} จาก {fileImportResult.summary.total} แถว
            </div>
            <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setFileImportResult(null)}>ปิด</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>✅ นำเข้าสำเร็จ: {fileImportResult.summary.imported}</span>
            <span>🕒 ยังไม่ระบุวันที่ (ข้ามไป): {fileImportResult.summary.missingDate}</span>
            <span>♻️ ซ้ำกับรายการเดิม: {fileImportResult.summary.duplicate}</span>
            <span>⚠️ ข้อมูลไม่ครบ: {fileImportResult.summary.invalid}</span>
          </div>
          {fileImportResult.rows.some((r) => r.status !== 'imported') && (
            <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {fileImportResult.rows.filter((r) => r.status !== 'imported').map((r) => (
                <div key={r.row} style={{ display: 'flex', gap: 10, padding: '6px 10px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--dim)', fontFamily: 'var(--mono)' }}>แถว {r.row}</span>
                  <span style={{ flex: 1 }}>{r.eventName}</span>
                  <span style={{ color: r.status === 'missing_date' ? 'var(--sv-accent)' : r.status === 'duplicate' ? 'var(--muted)' : 'var(--sv-danger)' }}>{r.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {err && <div className="form-error">{err}</div>}

      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: '1.2fr 2fr 1.5fr 1.8fr 1fr 0.6fr' }}>
          <div>วันที่จัดงาน</div>
          <div>ชื่อคอนเสิร์ต / ผู้นำเสนอ</div>
          <div>ศิลปิน</div>
          <div>สถานที่จัดงาน</div>
          <div>จังหวัด</div>
          <div style={{ textAlign: 'center' }}>จัดการ</div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', fontSize: 13 }}>
            กำลังโหลดปฏิทินคอนเสิร์ต...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>
            ไม่พบรายการคอนเสิร์ตในระบบ
          </div>
        ) : (
          filtered.map((item) => (
            <div className="sa-table-row" key={item.id} style={{ gridTemplateColumns: '1.2fr 2fr 1.5fr 1.8fr 1fr 0.6fr' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--sv-accent)', fontWeight: 600 }}>
                {item.dateLabel}
              </div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.eventName}</div>
              <div style={{ fontSize: 13, color: 'var(--sv-ok-soft)' }}>{item.artist}</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{item.venue}</div>
              <div style={{ fontSize: 12, color: 'var(--fg)', fontFamily: 'var(--mono)' }}>{item.province}</div>
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => handleDelete(item.id, item.eventName)}
                  title="ลบออกจากปฏิทิน"
                  style={{ border: 'none', background: 'rgba(239,68,68,0.15)', color: 'var(--sv-danger-soft)', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      </>
      )}

      {showAddModal && (
        <Modal title="เพิ่มงานคอนเสิร์ตลงปฏิทิน" sub="ข้อมูลภายในสำหรับทีมงาน ใช้เลี่ยงวันชนกัน" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAdd} className="sa-modal-body" style={{ display: 'grid', gap: 14 }}>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>
                นำเข้าจากลิงก์ (รองรับ theconcert.com, thaiticketmajor.com, ticketmelon.com):
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://www.theconcert.com/concert/xxxx"
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
                />
                <button type="button" className="btn-ghost" disabled={importState === 'loading' || !importUrl.trim()} onClick={handleImport}>
                  {importState === 'loading' ? 'กำลังดึง...' : 'ดึงข้อมูล'}
                </button>
              </div>
              {importErr && <div style={{ color: 'var(--sv-danger)', fontSize: 12, marginTop: 6 }}>{importErr}</div>}
              <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
                ระบบดึงเฉพาะชื่องาน/สถานที่แบบ best-effort จากหน้าเว็บต้นทาง — กรุณาตรวจสอบและแก้ไขข้อมูลด้านล่างให้ถูกต้องก่อนบันทึกเสมอ
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>วันที่จัดงาน (Date Label):</label>
              <input
                type="text"
                value={dateLabel}
                onChange={(e) => setDateLabel(e.target.value)}
                placeholder="เช่น 1 - 2 ส.ค. 2026"
                required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>ชื่อคอนเสิร์ต / ผู้นำเสนอ:</label>
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="เช่น F✦FOREVER 1st World Tour"
                required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>ศิลปิน:</label>
              <input
                type="text"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="เช่น F✦FOREVER"
                required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>สถานที่จัดงาน:</label>
              <input
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="เช่น อิมแพ็ค อารีนา เมืองทองธานี"
                required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>จังหวัด:</label>
              <input
                type="text"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                placeholder="เช่น กรุงเทพมหานคร, นนทบุรี, เชียงใหม่"
                required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Month Key (เช่น 2026-08):</label>
                <input
                  type="text"
                  value={monthKey}
                  onChange={(e) => setMonthKey(e.target.value)}
                  placeholder="2026-08"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Sort Date (เช่น 2026-08-01):</label>
                <input
                  type="text"
                  value={sortDate}
                  onChange={(e) => setSortDate(e.target.value)}
                  placeholder="2026-08-01"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
                />
              </div>
            </div>

            <div className="sa-modal-foot" style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setShowAddModal(false)}>ยกเลิก</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'กำลังบันทึก...' : '💾 บันทึกคอนเสิร์ต'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- tokens ---------- */
function TokensPanel({ tokens, onRevoke, onOpenNew }: { tokens: AccessTokenDto[]; onRevoke: (id: string) => void; onOpenNew: () => void }) {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (id: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500);
    });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px' }} onClick={onOpenNew}>
          <IconPlus /> สร้าง Token
        </button>
      </div>
      <div className="sa-table">
        <div className="sa-table-head">
          <div>Token / ผู้ถือ</div><div>อีเวนต์</div><div>บทบาท</div><div>หมดอายุ</div><div>สถานะ</div><div></div>
        </div>
        {tokens.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>ยังไม่มี Token</div>
        ) : (
          tokens.map((k) => {
            const isRevealed = !!revealed[k.id];
            return (
              <div className="sa-table-row" key={k.id}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220,
                      }}
                      title={isRevealed ? k.token ?? undefined : undefined}
                    >
                      {isRevealed && k.token ? k.token : k.tokenPreview}
                    </span>
                    {k.token && (
                      <>
                        <button
                          type="button"
                          className="sa-icon-btn"
                          style={{ width: 26, height: 26 }}
                          title={isRevealed ? 'ซ่อน Token' : 'แสดง Token เต็ม'}
                          onClick={() => setRevealed((r) => ({ ...r, [k.id]: !r[k.id] }))}
                        >
                          {isRevealed ? <IconEyeOff size={13} /> : <IconEye size={13} />}
                        </button>
                        <button
                          type="button"
                          className="sa-icon-btn"
                          style={{ width: 26, height: 26 }}
                          title="คัดลอก Token"
                          onClick={() => copy(k.id, k.token!)}
                        >
                          {copied === k.id ? <IconCheck size={13} /> : <IconCopy size={13} />}
                        </button>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{k.holderLabel || '—'}</div>
                </div>
                <div style={{ fontSize: 12.5, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.eventName}</div>
                <div><span className="status-chip">{ROLE_LABEL[k.role]}</span></div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
                  {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString('th-TH') : 'ไม่มีวันหมดอายุ'}
                </div>
                <div>
                  <span className={`status-chip ${k.active ? 'paid' : 'rejected'}`}>
                    {k.revoked ? 'เพิกถอนแล้ว' : k.expired ? 'หมดอายุ' : k.usedUp ? 'ใช้แล้ว' : 'ใช้งานได้'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {k.active && (
                    <button className="sa-icon-btn danger" title="เพิกถอน" onClick={() => onRevoke(k.id)}><IconTrash /></button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

/* ---------- audit ---------- */
function AuditPanel({
  rows, events, actions, eventFilter, actionFilter, onEventFilterChange, onActionFilterChange,
}: {
  rows: AuditRowDto[];
  events: TenantSummaryDto[];
  actions: string[];
  eventFilter: string;
  actionFilter: string;
  onEventFilterChange: (v: string) => void;
  onActionFilterChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="sa-audit-filters">
        <select value={eventFilter} onChange={(e) => onEventFilterChange(e.target.value)}>
          <option value="">ทุกอีเวนต์</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select value={actionFilter} onChange={(e) => onActionFilterChange(e.target.value)}>
          <option value="">ทุก Action</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      {rows.length === 0 ? <div className="admin-empty">ยังไม่มีบันทึกการใช้งาน</div> : rows.map((a) => (
        <div className="sa-audit-row" key={a.id}>
          <div className="sa-audit-icon"><IconAudit size={16} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{a.action}{a.detail ? ` — ${a.detail}` : ''}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 5, fontSize: 11.5, color: 'var(--dim)' }}>
              <span style={{ fontFamily: 'var(--mono)' }}>{a.actor}</span>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--sv-neutral-line)' }}></span>
              <span>{a.eventName ?? 'แพลตฟอร์ม'}</span>
            </div>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--dim)', flex: 'none', whiteSpace: 'nowrap' }}>
            {new Date(a.createdAt).toLocaleString('th-TH')}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- system ---------- */
function SystemPanel({ services }: { services: ServiceStatusDto[] }) {
  const color = (s: ServiceStatusDto['status']) => (s === 'operational' ? 'var(--sv-ok-soft)' : s === 'degraded' ? 'var(--sv-warn)' : 'var(--sv-danger)');
  const label = (s: ServiceStatusDto['status']) => (s === 'operational' ? 'ปกติ' : s === 'degraded' ? 'ลดประสิทธิภาพ' : 'ล่ม');
  return (
    <div className="sa-stat-grid">
      {services.map((v) => (
        <div key={v.name} className="sa-service-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{v.name}</div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--mono)', fontSize: 12.5, fontWeight: 600, color: color(v.status) }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color(v.status) }} />{label(v.status)}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>{v.detail}</div>
          {v.latencyMs !== null && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--dim)' }}>เวลาตอบสนอง</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 14, marginTop: 3 }}>{v.latencyMs} ms</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VendorBadge({ vendor }: { vendor: string }) {
  const v = vendor.toLowerCase();
  /*
   * TICK-140 — the four identities read from tokens rather than from hex chosen against a dark
   * panel. `:root` carries the values this badge has always drawn, so the classic console is
   * unchanged; `console/tokens.css` re-values them for the light canvas, where axe measured the
   * original `#fb923c` below 4.5:1 on its own wash.
   */
  let label = `[${vendor}]`;
  let tone = 'neutral';

  if (v === 'easyslip') { label = '[EasySlip]'; tone = 'easyslip'; }
  else if (v === 'thunder') { label = '[Thunder]'; tone = 'thunder'; }
  else if (v === 'manual') { label = '[Manual Staff]'; tone = 'manual'; }
  else if (v === 'unconfigured') { label = '[ยังไม่ได้ตั้งค่า]'; tone = 'unset'; }

  const bg = `var(--vendor-${tone}-bg)`;
  const color = `var(--vendor-${tone}-fg)`;
  const border = `1px solid var(--vendor-${tone}-border)`;

  return (
    <span style={{
      fontFamily: 'var(--mono)',
      fontSize: 11,
      padding: '2px 7px',
      borderRadius: 4,
      fontWeight: 600,
      display: 'inline-block',
      background: bg,
      color,
      border,
    }}>
      {label}
    </span>
  );
}

function SlipStatusBadge({ status }: { status: string }) {
  let label = status;
  let chipClass = 'pending_payment';

  if (status === 'verified') {
    label = 'ผ่าน (Verified)';
    chipClass = 'paid';
  } else if (status === 'duplicate') {
    label = 'สลิปซ้ำ (Duplicate)';
    chipClass = 'pending_payment';
  } else if (status === 'mismatch_amount') {
    label = 'ยอดไม่ตรง';
    chipClass = 'rejected';
  } else if (status === 'mismatch_receiver') {
    label = 'บัญชีรับไม่ตรง';
    chipClass = 'rejected';
  } else if (status === 'invalid_slip') {
    label = 'สลิปไม่ถูกต้อง';
    chipClass = 'rejected';
  } else if (status === 'quota_exceeded') {
    label = 'โควต้าเต็ม';
    chipClass = 'rejected';
  } else if (status === 'error') {
    label = 'เกิดข้อผิดพลาด';
    chipClass = 'rejected';
  }

  return <span className={`status-chip ${chipClass}`}>{label}</span>;
}

const ORDER_STATUS_LABEL: Record<SuperadminOperationDto['orderStatus'], string> = {
  pending_payment: 'รอชำระเงิน',
  awaiting_verification: 'รอตรวจสลิป',
  paid: 'ชำระแล้ว',
  expired: 'หมดเวลา',
  cancelled: 'ยกเลิก',
  rejected: 'ไม่ผ่าน',
};

function OperationsPanel({ token, events }: { token: string; events: TenantSummaryDto[] }) {
  const [rows, setRows] = useState<SuperadminOperationDto[]>([]);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>({});
  const [revealBusy, setRevealBusy] = useState('');
  const [revealError, setRevealError] = useState('');
  const [copiedLinkId, setCopiedLinkId] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    setRevealedLinks({});
    const timer = window.setTimeout(() => {
      superadminApi.listOperations(token, {
        q: search.trim() || undefined,
        eventId: eventFilter || undefined,
        status: orderStatusFilter || undefined,
        limit: 50,
        offset,
      }).then((page) => {
        if (!active) return;
        setRows(page.items);
        setNextOffset(page.nextOffset);
      }).catch(() => {
        if (active) setLoadError('โหลดข้อมูลธุรกรรมไม่สำเร็จ');
      }).finally(() => {
        if (active) setLoading(false);
      });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [token, search, eventFilter, orderStatusFilter, offset]);

  const revealLink = async (linkId: string) => {
    if (!window.confirm('เปิดเผยลิงก์บัตรฉุกเฉิน? การดำเนินการนี้จะถูกบันทึกใน Audit Log และลิงก์สามารถใช้เข้าถึงบัตรได้')) return;
    setRevealBusy(linkId);
    setRevealError('');
    try {
      const result = await superadminApi.revealTicketLink(token, linkId);
      setRevealedLinks((current) => ({ ...current, [linkId]: result.url }));
    } catch (error) {
      setRevealError(error instanceof ApiError ? error.message : 'ไม่สามารถเปิดเผยลิงก์กู้คืนได้');
    } finally {
      setRevealBusy('');
    }
  };

  const hideLink = (linkId: string) => {
    setRevealedLinks((current) => {
      const next = { ...current };
      delete next[linkId];
      return next;
    });
  };

  const copyLink = async (linkId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(linkId);
      window.setTimeout(() => setCopiedLinkId(''), 1500);
    } catch {
      setRevealError('คัดลอกไม่สำเร็จ กรุณาเลือกข้อความแล้วคัดลอกเอง');
    }
  };

  return (
    <div>
      <div className="sa-audit-filters sa-operations-filters">
        <input
          aria-label="ค้นหาธุรกรรมและการส่งบัตร"
          autoComplete="off"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setOffset(0); }}
          placeholder="ค้นหา Order, อีเวนต์, ชื่อ, เบอร์โทร หรืออีเมล..."
        />
        <select aria-label="กรองธุรกรรมตามอีเวนต์" value={eventFilter} onChange={(event) => { setEventFilter(event.target.value); setOffset(0); }}>
          <option value="">ทุกอีเวนต์</option>
          {events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
        </select>
        <select aria-label="กรองธุรกรรมตามสถานะออร์เดอร์" value={orderStatusFilter} onChange={(event) => { setOrderStatusFilter(event.target.value); setOffset(0); }}>
          <option value="">ทุกสถานะออร์เดอร์</option>
          {Object.entries(ORDER_STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      <div className="sa-operations-table" role="table" aria-label="ธุรกรรม การตรวจสลิป และการส่งบัตร">
        <div className="sa-operations-head" role="row">
          {['อัปเดตล่าสุด', 'อีเวนต์ / ออร์เดอร์', 'ข้อมูลผู้จอง', 'การชำระเงิน', 'การส่งบัตร', 'Token กู้คืน'].map((heading) => (
            <div key={heading} role="columnheader">{heading}</div>
          ))}
        </div>
        {loading ? (
          <div role="row"><div className="admin-empty" role="cell" aria-live="polite">กำลังโหลดธุรกรรม…</div></div>
        ) : loadError ? (
          <div role="row"><div className="form-error" role="cell" aria-live="assertive">{loadError}</div></div>
        ) : rows.length === 0 ? (
          <div role="row"><div className="admin-empty" role="cell">ไม่พบธุรกรรมที่ตรงกับตัวกรอง</div></div>
        ) : rows.map((row) => {
          const emailDelivery = row.deliveries.find((delivery) => delivery.channel === 'email');
          const smsDelivery = row.deliveries.find((delivery) => delivery.channel === 'sms');
          const revealedUrl = row.ticketLinkId ? revealedLinks[row.ticketLinkId] : undefined;
          return (
            <div className="sa-operations-row" role="row" key={row.orderId}>
              <div role="cell" data-label="อัปเดตล่าสุด" className="sa-operations-time">
                {new Date(row.latestActivityAt).toLocaleString('th-TH')}
              </div>
              <div role="cell" data-label="อีเวนต์ / ออร์เดอร์">
                <div className="sa-operations-primary">{row.eventName}</div>
                <div className="sa-operations-mono">{row.orderCode}</div>
                <span className={`status-chip ${row.orderStatus === 'paid' ? 'paid' : row.orderStatus === 'rejected' || row.orderStatus === 'cancelled' || row.orderStatus === 'expired' ? 'rejected' : 'pending_payment'}`}>
                  {ORDER_STATUS_LABEL[row.orderStatus]}
                </span>
              </div>
              <div role="cell" data-label="ข้อมูลผู้จอง" className="sa-operations-contact">
                <div className="sa-operations-primary">{row.customerName || 'ยังไม่มีข้อมูลผู้จอง'}</div>
                {row.customerPhone && <a href={`tel:${row.customerPhone}`}>{row.customerPhone}</a>}
                {row.customerEmail && <a href={`mailto:${row.customerEmail}`}>{row.customerEmail}</a>}
                <div className="sa-operations-total">{fmt(row.total)}</div>
              </div>
              <div role="cell" data-label="การชำระเงิน">
                {row.slip ? (
                  <div className="sa-operations-stack">
                    <VendorBadge vendor={row.slip.vendor} />
                    <SlipStatusBadge status={row.slip.status} />
                    <span className="sa-operations-note">ตรวจ {row.slip.attemptCount} ครั้ง{row.slip.transactionRefMasked ? ` · ${row.slip.transactionRefMasked}` : ''}</span>
                  </div>
                ) : <span className="sa-operations-empty">ยังไม่มีผลตรวจสลิป</span>}
              </div>
              <div role="cell" data-label="การส่งบัตร" className="sa-operations-stack">
                {emailDelivery && <div className="sa-delivery-line"><strong>Email</strong><DeliveryStatusBadge status={emailDelivery.status} /></div>}
                {smsDelivery && <div className="sa-delivery-line"><strong>SMS</strong><DeliveryStatusBadge status={smsDelivery.status} /></div>}
                {!emailDelivery && !smsDelivery && <span className="sa-operations-empty">ยังไม่มีรายการส่ง</span>}
                {row.ticketDeliveryBlock && (
                  <span className="form-warn" role="note">ส่งตั๋วไม่ได้ — {row.ticketDeliveryBlock.message}</span>
                )}
              </div>
              <div role="cell" data-label="Token กู้คืน" className="sa-operations-recovery">
                {row.ticketLinkId ? (
                  <>
                    <div className="sa-operations-mono">{row.ticketTokenMasked ?? 'Token รุ่นเดิม — ไม่มี Preview'}</div>
                    {row.ticketLinkExpiresAt && <div className="sa-operations-note">หมดอายุ {new Date(row.ticketLinkExpiresAt).toLocaleString('th-TH')}</div>}
                    {row.ticketLinkFirstOpenedAt && <div className="sa-operations-note">เปิดแล้ว {new Date(row.ticketLinkFirstOpenedAt).toLocaleString('th-TH')}</div>}
                    {revealedUrl ? (
                      <div className="sa-recovery-reveal">
                        <input aria-label={`ลิงก์กู้คืน ${row.orderCode}`} readOnly value={revealedUrl} onFocus={(event) => event.currentTarget.select()} />
                        <button className="btn-ghost" onClick={() => copyLink(row.ticketLinkId!, revealedUrl)}>
                          {copiedLinkId === row.ticketLinkId ? 'คัดลอกแล้ว' : 'คัดลอกลิงก์'}
                        </button>
                        <button className="btn-ghost" onClick={() => hideLink(row.ticketLinkId!)}>ซ่อน</button>
                      </div>
                    ) : (
                      <button className="btn-ghost" disabled={revealBusy === row.ticketLinkId} onClick={() => revealLink(row.ticketLinkId!)}>
                        {revealBusy === row.ticketLinkId ? 'กำลังเปิดเผย…' : 'เปิดเผยเพื่อกู้คืน'}
                      </button>
                    )}
                  </>
                ) : <span className="sa-operations-empty">ยังไม่มี Token</span>}
              </div>
            </div>
          );
        })}
      </div>
      {!loading && !loadError && rows.length > 0 && (
        <nav className="sa-operations-pagination" aria-label="หน้ารายการธุรกรรม">
          <button className="btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>หน้าก่อนหน้า</button>
          <span>รายการ {offset + 1}–{offset + rows.length}</span>
          <button className="btn-ghost" disabled={nextOffset === null} onClick={() => nextOffset !== null && setOffset(nextOffset)}>หน้าถัดไป</button>
        </nav>
      )}
      {revealError && <div className="form-error" role="alert" style={{ marginTop: 12 }}>{revealError}</div>}
    </div>
  );
}

/* ---------- slips ---------- */
function SlipsPanel({ rows }: { rows: SlipVerificationAuditDto[] }) {
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const events = Array.from(
    new Map(
      rows
        .filter((r) => r.eventSlug || r.eventName)
        .map((r) => [r.eventSlug || r.eventName, { slug: r.eventSlug, name: r.eventName || r.eventSlug }])
    ).values()
  );

  const filtered = rows.filter((r) => {
    if (eventFilter && r.eventSlug !== eventFilter && r.eventName !== eventFilter) return false;
    if (vendorFilter && r.vendor.toLowerCase() !== vendorFilter.toLowerCase()) return false;
    if (statusFilter && r.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const match =
        r.orderCode.toLowerCase().includes(q) ||
        (r.transactionRefMasked && r.transactionRefMasked.toLowerCase().includes(q)) ||
        r.eventName.toLowerCase().includes(q) ||
        r.eventSlug.toLowerCase().includes(q) ||
        r.vendor.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="sa-audit-filters" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          aria-label="ค้นหาผลการตรวจสอบสลิป"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหา Order Code, Ref ที่ปิดบัง, อีเวนต์..."
          style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        />
        <select
          aria-label="กรองผลตรวจสอบสลิปตามอีเวนต์"
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        >
          <option value="">ทุกอีเวนต์</option>
          {events.map((ev) => (
            <option key={ev.slug || ev.name} value={ev.slug || ev.name}>
              {ev.name}
            </option>
          ))}
        </select>
        <select
          aria-label="กรองผลตรวจสอบสลิปตาม Vendor"
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        >
          <option value="">ทุก Vendor</option>
          <option value="easyslip">EasySlip</option>
          <option value="thunder">Thunder</option>
          <option value="manual">Manual Staff</option>
          <option value="mock">Mock</option>
        </select>
        <select
          aria-label="กรองผลตรวจสอบสลิปตามสถานะ"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        >
          <option value="">ทุกสถานะ</option>
          <option value="verified">ผ่าน (Verified)</option>
          <option value="duplicate">สลิปซ้ำ (Duplicate)</option>
          <option value="mismatch_amount">ยอดไม่ตรง</option>
          <option value="mismatch_receiver">บัญชีรับไม่ตรง</option>
          <option value="invalid_slip">สลิปไม่ถูกต้อง</option>
          <option value="quota_exceeded">โควต้าเต็ม</option>
          <option value="error">เกิดข้อผิดพลาด</option>
        </select>
      </div>

      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: '1.2fr 1.2fr 1.3fr 1fr 1fr 1.4fr' }}>
          <div>วันเวลา</div>
          <div>อีเวนต์</div>
          <div>ออร์เดอร์</div>
          <div>Vendor</div>
          <div>สถานะ</div>
          <div>ข้อมูลที่ปิดบัง / ยอดเงิน</div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>
            ไม่พบข้อมูลการตรวจสอบสลิป
          </div>
        ) : (
          filtered.map((item) => (
            <div className="sa-table-row" key={item.id} style={{ gridTemplateColumns: '1.2fr 1.2fr 1.3fr 1fr 1fr 1.4fr' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>
                {new Date(item.createdAt).toLocaleString('th-TH')}
              </div>
              <div style={{ fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <div>{item.eventName}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--dim)' }}>{item.eventSlug}</div>
              </div>
              <div style={{ fontSize: 12.5, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{item.orderCode}</div>
              </div>
              <div>
                <VendorBadge vendor={item.vendor} />
              </div>
              <div>
                <SlipStatusBadge status={item.status} />
              </div>
              <div style={{ fontSize: 12 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Ref: {item.transactionRefMasked || '—'}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>บัญชี: {item.receiverAccountMasked || '—'}</div>
                <div style={{ fontWeight: 600, color: 'var(--fg)', marginTop: 2 }}>{item.amount ? fmt(item.amount) : '—'}</div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
}

/* ---------- notification delivery ledger ---------- */
function DeliveryStatusBadge({ status }: { status: string }) {
  const value = status.toLowerCase();
  let label = status;
  let chipClass = 'pending_payment';
  if (value.includes('deliver')) { label = 'ส่งถึงแล้ว'; chipClass = 'paid'; }
  else if (value === 'accepted' || value === 'email.sent') { label = 'ผู้ให้บริการรับแล้ว'; chipClass = 'paid'; }
  else if (value === 'queued') { label = 'รอส่ง'; }
  else if (value.includes('bounce')) { label = 'ตีกลับ'; chipClass = 'rejected'; }
  else if (value.includes('complain')) { label = 'ร้องเรียน'; chipClass = 'rejected'; }
  else if (value.includes('fail') || value.includes('reject')) { label = 'ส่งไม่สำเร็จ'; chipClass = 'rejected'; }
  return <span className={`status-chip ${chipClass}`}>{label}</span>;
}

function DeliveryPanel({ rows, token }: { rows: NotificationDeliveryDto[]; token: string }) {
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [revealedLink, setRevealedLink] = useState<{ id: string; url: string } | null>(null);
  const [revealBusy, setRevealBusy] = useState('');
  const [revealError, setRevealError] = useState('');
  const revealLink = async (linkId: string) => {
    setRevealBusy(linkId); setRevealError('');
    try { setRevealedLink({ id: linkId, url: (await superadminApi.revealTicketLink(token, linkId)).url }); }
    catch { setRevealError('ไม่สามารถเปิดเผยลิงก์ได้'); }
    finally { setRevealBusy(''); }
  };
  const events = Array.from(new Map(rows.map((row) => [row.eventSlug || row.eventName || row.eventId, {
    id: row.eventSlug || row.eventName || row.eventId,
    name: row.eventName || row.eventSlug || row.eventId,
  }])).values());
  const filtered = rows.filter((row) => {
    if (eventFilter && eventFilter !== (row.eventSlug || row.eventName || row.eventId)) return false;
    if (channelFilter && row.channel !== channelFilter) return false;
    if (statusFilter && row.status.toLowerCase() !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    return !q || [row.orderCode, row.eventName, row.eventSlug, row.channel, row.provider, row.recipientMasked, row.status, row.lastErrorCode]
      .filter(Boolean).some((value) => String(value).toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="sa-audit-filters" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          aria-label="ค้นหาสถานะการส่งบัตร"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหา Order Code, อีเวนต์, ผู้รับที่ปิดบัง..."
          style={{ flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}
        />
        <select aria-label="กรองการส่งบัตรตามอีเวนต์" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}>
          <option value="">ทุกอีเวนต์</option>
          {events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
        </select>
        <select aria-label="กรองการส่งบัตรตามช่องทาง" value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}>
          <option value="">ทุกช่องทาง</option><option value="email">Email</option><option value="sms">SMS</option>
        </select>
        <select aria-label="กรองการส่งบัตรตามสถานะ" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--panel-bg)', border: '1px solid var(--border)', color: 'var(--fg)', fontSize: 13 }}>
          <option value="">ทุกสถานะ</option><option value="queued">รอส่ง</option><option value="accepted">ผู้ให้บริการรับแล้ว</option><option value="delivery">ส่งถึงแล้ว (SMS)</option><option value="email.delivered">ส่งถึงแล้ว (Email)</option><option value="email.bounced">ตีกลับ</option><option value="failed">ส่งไม่สำเร็จ</option>
        </select>
      </div>
      <div className="sa-table">
        <div className="sa-table-head" style={{ gridTemplateColumns: '1.1fr 1.2fr 1fr .8fr .9fr 1.2fr 1fr' }}>
          <div>วันเวลา</div><div>อีเวนต์</div><div>ออร์เดอร์</div><div>ช่องทาง</div><div>ผู้ให้บริการ</div><div>ผู้รับที่ปิดบัง</div><div>สถานะ</div>
        </div>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--dim)', fontSize: 13.5 }}>ยังไม่มีประวัติการส่งบัตร</div>
        ) : filtered.map((item) => (
          <div className="sa-table-row" key={item.id} style={{ gridTemplateColumns: '1.1fr 1.2fr 1fr .8fr .9fr 1.2fr 1fr' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>{formatDeliveryTimestamp(item.lastProviderTime ?? item.lastReceivedAt ?? item.createdAt)}</div>
            <div style={{ fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}><div>{item.eventName || '—'}</div><div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--dim)' }}>{item.eventSlug || ''}</div></div>
            <div style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 12.5 }}>{item.orderCode}</div>
            <div style={{ textTransform: 'uppercase', fontSize: 12.5, fontWeight: 600 }}>{item.channel}</div>
            <div style={{ fontSize: 12.5 }}>{item.provider}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{item.recipientMasked}</div>
            <div><DeliveryStatusBadge status={item.status} />{item.lastErrorCode && <div style={{ fontSize: 10.5, color: 'var(--danger)', marginTop: 4 }}>{item.lastErrorCode}</div>}</div>
            {item.ticketLinkId && <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
              {revealedLink?.id === item.ticketLinkId ? (
                <div className="sa-token-reveal"><div className="value-row"><input className="value" readOnly value={revealedLink.url} onFocus={(e) => e.currentTarget.select()} /><button className="btn-ghost" onClick={() => navigator.clipboard.writeText(revealedLink.url)}>คัดลอก</button></div></div>
              ) : <button className="btn-ghost" disabled={revealBusy === item.ticketLinkId} onClick={() => revealLink(item.ticketLinkId!)}>{revealBusy === item.ticketLinkId ? 'กำลังเปิดเผย…' : 'เปิดเผย short URL'}</button>}
            </div>}
          </div>
        ))}
      </div>
      {revealError && <div className="form-error" style={{ marginTop: 12 }}>{revealError}</div>}
    </div>
  );
}

function formatDeliveryTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('th-TH');
}

/* ---------- new event modal ---------- */
function NewEventModal({ token, defaultEvent, onClose, onCreated }: {
  token: string;
  /**
   * TICK-127 — the event `template: 'clone'` actually copies.
   *
   * The server clones whichever event carries `is_default`, and ignores any source the caller sends.
   * "อีเวนต์หลัก" was true and unreadable: on a platform with six events nobody can tell which one
   * that is from the label alone, and a provisioning choice made blind is a choice made wrong.
   */
  defaultEvent: { name: string } | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [eventDate, setEventDate] = useState('');
  /*
   * TICK-107 — the label shown to buyers, kept separate from the date the system reasons about.
   *
   * It used to be computed from `eventDate` with no way to change it, which silently made every
   * event a one-day event on the storefront: Moonfield runs 21–22 November and could not say so.
   * The API has accepted a free string since the beginning; only the form was narrower than the
   * contract. Suggested rather than fixed, following `slug` and the reference prefix above — the
   * usual case still needs no typing, and the unusual one is now reachable.
   */
  const [dateLabel, setDateLabel] = useState('');
  const [dateLabelTouched, setDateLabelTouched] = useState(false);
  const [transactionReferencePrefix, setTransactionReferencePrefix] = useState('');
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [plan, setPlan] = useState<'standard' | 'pro'>('standard');
  const [template, setTemplate] = useState<'blank' | 'clone' | 'festival' | 'saved'>('blank');
  const [templates, setTemplates] = useState<EventTemplateDto[]>([]);
  const [templateSourceId, setTemplateSourceId] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [createAsUat, setCreateAsUat] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<CreateEventResultDto | null>(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    superadminApi.listTemplates(token).then((rows) => {
      setTemplates(rows);
      if (rows.length > 0) setTemplateSourceId(rows[0].id);
    }).catch(() => {});
  }, [token]);

  const autoSlug = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  const suggestPrefix = (eventName: string, date: string) => {
    const words = eventName.match(/[A-Za-z0-9]+/g) ?? [];
    const initials = words.filter((word) => !/^\d+$/.test(word)).slice(0, 2).map((word) => word[0]).join('').toUpperCase() || 'JT';
    const year = (date ? date.slice(2, 4) : String(new Date().getFullYear()).slice(-2));
    return `${initials}${year}`.replace(/[^A-Z0-9]/g, '').slice(0, 8);
  };
  // "2026-12-14" -> "14 ธ.ค. 2569" — matches the Thai-Buddhist label format used across the app.
  const suggestDateLabel = (date: string) => (date
    ? new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${date}T00:00:00`))
    : '');

  const submit = async () => {
    if (!eventDate) { setErr('กรุณาระบุวันจัดงาน'); return; }
    if (!/^[A-Z0-9]{2,8}$/.test(transactionReferencePrefix)) {
      setErr('รหัสอ้างอิงต้องเป็น A-Z หรือ 0-9 ความยาว 2–8 ตัวอักษร');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const r = await superadminApi.createEvent(token, {
        name, slug: slug || autoSlug(name), dateLabel, eventDate,
        /*
         * TICK-126 — sent only when somebody actually typed it.
         *
         * The box is pre-filled with a suggestion, and a suggestion posted back reads to the server
         * as a deliberate choice — which is the one thing it refuses when another event already
         * holds that prefix. Leaving it out lets the server suggest and step aside from a
         * collision on its own, so a form nobody edited cannot produce an error about a value
         * nobody picked.
         */
        transactionReferencePrefix: prefixTouched ? transactionReferencePrefix : undefined,
        plan, ownerEmail, template,
        templateSourceId: template === 'saved' ? templateSourceId : undefined,
        salesMode: createAsUat ? 'uat' : 'public',
      });
      setResult(r);
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'สร้างอีเวนต์ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const copy = (key: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    });
  };

  return (
    <Modal title="สร้างอีเวนต์ใหม่" sub="Provision workspace ใหม่บนแพลตฟอร์ม" onClose={onClose}>
      {!result ? (
        <>
          <div className="sa-modal-body">
            <Field label="ชื่ออีเวนต์">
              <input value={name} onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(autoSlug(e.target.value));
                if (!prefixTouched) setTransactionReferencePrefix(suggestPrefix(e.target.value, eventDate));
              }} placeholder="เช่น Neon Sunset Bangkok" />
            </Field>
            <Field label="Slug">
              <input value={slug} onChange={(e) => { setSlug(autoSlug(e.target.value)); setSlugTouched(true); }} placeholder="neon-sunset" style={{ fontFamily: 'var(--mono)' }} />
            </Field>
            <div className="sa-field-row">
              <Field label="วันจัดงาน">
                <input type="date" aria-label="วันจัดงาน" required value={eventDate} onChange={(e) => {
                  setEventDate(e.target.value);
                  if (!prefixTouched) setTransactionReferencePrefix(suggestPrefix(name, e.target.value));
                  if (!dateLabelTouched) setDateLabel(suggestDateLabel(e.target.value));
                }} style={{ fontFamily: 'var(--mono)' }} />
                {pastDateWarning(eventDate) && <div className="form-warn" role="note">{pastDateWarning(eventDate)}</div>}
              </Field>
              <Field label="แพ็กเกจ">
                <select value={plan} onChange={(e) => setPlan(e.target.value as 'standard' | 'pro')}>
                  <option value="standard">Standard · 3.5% ต่อบัตร</option>
                  <option value="pro">Pro · 2.5% + ค่าธรรมเนียมรายเดือน</option>
                </select>
              </Field>
            </div>
            <Field label="วันที่สำหรับแสดงผล">
              <input
                value={dateLabel}
                aria-label="วันที่สำหรับแสดงผล"
                onChange={(e) => { setDateLabel(e.target.value.slice(0, 60)); setDateLabelTouched(true); }}
                placeholder="เช่น 21–22 พ.ย. 2569"
                maxLength={60}
              />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                ข้อความที่ลูกค้าเห็นบนหน้าขายและหน้ารวมอีเวนต์ · เติมจากวันจัดงานให้อัตโนมัติ แก้เป็นช่วงวันได้ · ไม่เกิน 60 ตัวอักษร
              </div>
            </Field>
            <Field label="รหัสอ้างอิงธุรกรรม">
              <input
                value={transactionReferencePrefix}
                aria-label="รหัสอ้างอิงธุรกรรม"
                required
                onChange={(e) => {
                  setTransactionReferencePrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
                  setPrefixTouched(true);
                }}
                placeholder="เช่น NS26"
                minLength={2}
                maxLength={8}
                pattern="[A-Z0-9]{2,8}"
                style={{ fontFamily: 'var(--mono)' }}
              />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                ใช้ A–Z และ 0–9 จำนวน 2–8 ตัว · ล็อกหลังมีออร์เดอร์แรก ·
                {prefixTouched
                  ? ' รหัสที่งานอื่นใช้อยู่แล้วจะถูกปฏิเสธพร้อมบอกว่างานไหนถืออยู่'
                  : ' ถ้าไม่แก้ ระบบจะเลือกให้และเลี่ยงรหัสที่ซ้ำกับงานอื่นเอง'}
              </div>
            </Field>
            <Field label="เริ่มจากเทมเพลต">
              <select value={template} onChange={(e) => setTemplate(e.target.value as typeof template)}>
                <option value="blank">Blank — เริ่มจากศูนย์</option>
                <option value="clone" disabled={!defaultEvent}>
                  {defaultEvent
                    ? `Clone จาก “${defaultEvent.name}” (อีเวนต์หลัก)`
                    : 'Clone จากอีเวนต์หลัก (ยังไม่มีอีเวนต์หลัก)'}
                </option>
                <option value="festival">Template: เทศกาลกลางแจ้ง</option>
                <option value="saved" disabled={templates.length === 0}>
                  Template ที่บันทึกไว้{templates.length === 0 ? ' (ยังไม่มี)' : ''}
                </option>
              </select>
            </Field>
            {template === 'saved' && templates.length > 0 && (
              <Field label="เลือกเทมเพลต">
                <select value={templateSourceId} onChange={(e) => setTemplateSourceId(e.target.value)}>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="อีเมล Owner (เจ้าของอีเวนต์)">
              <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@example.com" />
            </Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
              <input type="checkbox" checked={createAsUat} onChange={(e) => setCreateAsUat(e.target.checked)} />
              สร้างเป็น UAT (ไม่แสดงในหน้ารวมอีเวนต์สาธารณะ ต้องเปลี่ยนโหมดเองภายหลังจึงจะขายได้)
            </label>
            {err && <div className="form-error">{err}</div>}
          </div>
          <div className="sa-modal-foot">
            <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
            <button className="btn-primary" style={{ padding: '11px 20px' }} disabled={busy || !name.trim()} onClick={submit}>
              {busy ? 'กำลังสร้าง…' : 'Provision อีเวนต์'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="sa-modal-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <div style={{ width: 40, height: 40, flex: 'none', borderRadius: 11, background: 'var(--sv-wash-ok-deep)', border: '1px solid var(--sv-wash-ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sv-ok-soft)' }}>
                <IconCheck size={20} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Provision สำเร็จ</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{result.event.name} ถูกเพิ่มเข้าแพลตฟอร์มแล้ว</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { key: 'landingUrl', label: 'Landing Page URL', path: `/e/${result.event.slug}` },
                { key: 'adminFullUrl', label: 'Admin URL', path: result.adminUrl },
                { key: 'checkinUrl', label: 'Check-in URL', path: `/e/${result.event.slug}/staff/checkin` },
              ].map((row) => {
                const full = `${window.location.origin}${row.path}`;
                return (
                  <div className="sa-token-reveal" key={row.key}>
                    <div className="label">{row.label}</div>
                    <div className="value-row">
                      <a href={row.path} target="_blank" rel="noreferrer" className="value">
                        {full}
                      </a>
                      <button className="btn-ghost" style={{ padding: '6px 10px', fontSize: 11.5 }} onClick={() => copy(row.key, full)}>
                        {copied === row.key ? 'คัดลอกแล้ว' : 'คัดลอก'}
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="sa-token-reveal">
                <div className="label">Owner Token — แสดงเต็มเพียงครั้งเดียว</div>
                <div className="value-row">
                  <span className="value">{result.ownerToken}</span>
                  <button className="btn-ghost" style={{ padding: '6px 10px', fontSize: 11.5 }} onClick={() => copy('token', result.ownerToken)}>
                    {copied === 'token' ? 'คัดลอกแล้ว' : 'คัดลอก'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="sa-modal-foot">
            <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={onClose}>เสร็จสิ้น</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ---------- new token modal ---------- */
function NewTokenModal({
  token, events, onClose, onCreated,
}: {
  token: string; events: TenantSummaryDto[]; onClose: () => void; onCreated: () => void;
}) {
  const [eventId, setEventId] = useState(events[0]?.id ?? '');
  const [role, setRole] = useState<AccessTokenDto['role']>('staff');
  /*
   * `useState` reads the list once, at mount. Open this modal while the events are still loading —
   * which is a race, so it happens on a slow machine and never on a fast one — and `eventId` is
   * fixed at `''` for the life of the dialog. The `<select>` then receives its options, and a
   * `<select>` whose value matches no option displays the *first* one: the screen says an event is
   * chosen while the state says none is, and the only symptom is "กรุณาเลือกอีเวนต์" on a form that
   * looks completely filled in.
   *
   * Found by CI, on the e2e added for TICK-142: the GitHub runner is slow enough to lose the race
   * that no laptop here ever lost.
   */
  useEffect(() => {
    if (!eventId && events.length > 0) setEventId(events[0].id);
  }, [events, eventId]);
  const [holderLabel, setHolderLabel] = useState('');
  const [expiry, setExpiry] = useState<'24h' | '7d' | '30d' | 'event' | 'never'>('30d');
  const [kind, setKind] = useState<'reusable' | 'single'>('reusable');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<CreateTokenResultDto | null>(null);
  const [copied, setCopied] = useState(false);

  const roles: { key: AccessTokenDto['role']; label: string; desc: string }[] = [
    { key: 'owner', label: 'Owner', desc: 'สิทธิ์เต็ม ในอีเวนต์นั้น' },
    { key: 'staff', label: 'Staff', desc: 'จัดการออร์เดอร์ / บัตร / เนื้อหา' },
    { key: 'checkin', label: 'Check-in', desc: 'เช็กอินหน้างานเท่านั้น' },
    { key: 'sales', label: 'การขาย', desc: 'เห็นแค่ภาพรวมและตรวจสอบสลิป' },
  ];

  const submit = async () => {
    if (!eventId) { setErr('กรุณาเลือกอีเวนต์'); return; }
    setBusy(true);
    setErr('');
    try {
      const r = await superadminApi.createToken(token, { eventId, role, holderLabel, kind, expiry });
      setResult(r);
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'สร้าง Token ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Modal title="สร้าง Token การเข้าถึง" sub="ผูกกับอีเวนต์และบทบาทที่เลือก" onClose={onClose}>
      {!result ? (
        <>
          <div className="sa-modal-body">
            <Field label="อีเวนต์">
              <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
                {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </Field>
            <Field label="บทบาท">
              {/* A group, not a control: `aria-labelledby` from `Field` names the set. */}
              <div className="sa-role-grid" role="group">
                {roles.map((r) => (
                  <button key={r.key} type="button" className={`sa-role-btn ${role === r.key ? 'active' : ''}`} onClick={() => setRole(r.key)}>
                    <span className="status-chip">{r.label}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{r.desc}</span>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="ผู้ถือ Token (ป้ายกำกับ)">
              <input value={holderLabel} onChange={(e) => setHolderLabel(e.target.value)} placeholder="เช่น ทีมหน้างาน โซน A" />
            </Field>
            <div className="sa-field-row">
              <Field label="หมดอายุ">
                <select value={expiry} onChange={(e) => setExpiry(e.target.value as typeof expiry)}>
                  <option value="24h">24 ชั่วโมง</option>
                  <option value="7d">7 วัน</option>
                  <option value="30d">30 วัน</option>
                  <option value="never">ไม่มีวันหมดอายุ</option>
                </select>
              </Field>
              <Field label="ประเภท">
                <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                  <option value="single">ลิงก์เชิญ · ใช้ครั้งเดียว</option>
                  <option value="reusable">Key · ใช้ซ้ำได้</option>
                </select>
              </Field>
            </div>
            {err && <div className="form-error">{err}</div>}
          </div>
          <div className="sa-modal-foot">
            <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
            <button className="btn-primary" style={{ padding: '11px 20px' }} disabled={busy} onClick={submit}>
              {busy ? 'กำลังสร้าง…' : 'สร้าง Token'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="sa-modal-body">
            <div style={{ fontSize: 12.5, color: 'var(--sv-warn)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              คัดลอกเก็บไว้เดี๋ยวนี้ — Token จะแสดงเต็มเพียงครั้งเดียว
            </div>
            <div className="sa-token-reveal">
              <div className="label">Access Token</div>
              <div className="value-row">
                <span className="value">{result.token}</span>
                <button className="btn-ghost" style={{ padding: '6px 10px', fontSize: 11.5 }} onClick={copy}>
                  {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
                </button>
              </div>
            </div>
          </div>
          <div className="sa-modal-foot">
            <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={onClose}>เสร็จสิ้น</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ---------- reset / delete confirm (with an export-first reminder) ---------- */
function SaveTemplateModal({
  token, event, onClose, onSaved,
}: {
  token: string;
  event: TenantSummaryDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(event.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await superadminApi.saveEventAsTemplate(token, event.id, name.trim());
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกเทมเพลตไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="บันทึกเป็นเทมเพลต" sub={`คัดลอก Landing Page และตั๋วของ "${event.name}" ไว้ใช้สร้างอีเวนต์ใหม่ในภายหลัง`} onClose={onClose}>
      <div className="sa-modal-body">
        <Field label="ชื่อเทมเพลต">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น เทศกาลกลางแจ้ง Standard" />
        </Field>
        {err && <div className="form-error">{err}</div>}
      </div>
      <div className="sa-modal-foot">
        <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
        <button className="btn-primary" style={{ padding: '11px 20px' }} disabled={busy || !name.trim()} onClick={submit}>
          {busy ? 'กำลังบันทึก…' : 'บันทึกเทมเพลต'}
        </button>
      </div>
    </Modal>
  );
}

function DangerConfirmModal({
  token, event, mode, onClose, onDone,
}: {
  token: string;
  event: TenantSummaryDto;
  mode: 'reset' | 'delete';
  onClose: () => void;
  onDone: () => void;
}) {
  const [exported, setExported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const doExport = async () => {
    setErr('');
    try {
      await superadminApi.exportEventOrders(token, event.id);
      setExported(true);
    } catch {
      setErr('Export ไม่สำเร็จ');
    }
  };

  const confirm = async () => {
    setBusy(true);
    setErr('');
    try {
      if (mode === 'reset') await superadminApi.resetEventTransactions(token, event.id);
      else await superadminApi.deleteEvent(token, event.id);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : mode === 'reset' ? 'รีเซ็ตไม่สำเร็จ' : 'ลบไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'reset' ? 'รีเซ็ต Transaction' : 'ลบอีเวนต์';
  const sub =
    mode === 'reset'
      ? `ลบออร์เดอร์ทั้งหมดและรีเซ็ตจำนวนบัตรที่ขายแล้วของ "${event.name}" — ทำแล้วกู้คืนไม่ได้`
      : `ลบ "${event.name}" ทั้งหมด รวมถึงตั๋ว ออร์เดอร์ คูปอง และ Token — ทำแล้วกู้คืนไม่ได้`;

  return (
    <Modal title={title} sub={sub} onClose={onClose}>
      <div className="sa-modal-body">
        <div style={{ fontSize: 12.5, color: 'var(--sv-warn)', lineHeight: 1.6, marginBottom: 16 }}>
          แนะนำให้ Export Transaction เก็บไว้ก่อน — ข้อมูลจะหายถาวรหลังจาก{mode === 'reset' ? 'รีเซ็ต' : 'ลบอีเวนต์'}
        </div>
        <button
          type="button"
          className="btn-ghost"
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          onClick={doExport}
        >
          {exported ? <IconCheck size={14} /> : null} Export Transaction (CSV)
        </button>
        {err && <div className="form-error" style={{ marginTop: 12 }}>{err}</div>}
      </div>
      <div className="sa-modal-foot">
        <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
        <button className="btn-danger" style={{ padding: '11px 20px' }} disabled={busy} onClick={confirm}>
          {busy ? 'กำลังดำเนินการ…' : mode === 'reset' ? 'ยืนยันรีเซ็ต' : 'ยืนยันลบอีเวนต์'}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- root ---------- */
export default function Superadmin() {
  useEffect(() => { document.title = 'JamTick — SuperAdmin'; }, []);

  const [token, setToken] = useState(
    () => sessionStorage.getItem('jamtick.superadminToken') ?? localStorage.getItem('superadmin_token') ?? localStorage.getItem('jamtick.superadminToken') ?? ''
  );
  const [tokenInput, setTokenInput] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [tab, setTab] = useState<Tab>('overview');
  // Persisted: an operator who collapsed the rail to read a diagram should not have to redo it on
  // every visit. sessionStorage (not local) to match how the superadmin token is already scoped.
  const [navCollapsed, setNavCollapsed] = useState(() => sessionStorage.getItem('jamtick.saNavCollapsed') === '1');
  useEffect(() => { sessionStorage.setItem('jamtick.saNavCollapsed', navCollapsed ? '1' : '0'); }, [navCollapsed]);
  const [overviewRefreshing, setOverviewRefreshing] = useState(false);

  const [overview, setOverview] = useState<PlatformOverviewDto | null>(null);
  const [events, setEvents] = useState<TenantSummaryDto[]>([]);
  const [tokens, setTokens] = useState<AccessTokenDto[]>([]);
  const [leads, setLeads] = useState<OrganizerLeadDto[]>([]);
  const [audit, setAudit] = useState<AuditRowDto[]>([]);
  const [error, setError] = useState('');

  const [showNewEvent, setShowNewEvent] = useState(false);
  const [showNewToken, setShowNewToken] = useState(false);
  const [resetTarget, setResetTarget] = useState<TenantSummaryDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantSummaryDto | null>(null);
  const [templateTarget, setTemplateTarget] = useState<TenantSummaryDto | null>(null);
  const [statusBusy, setStatusBusy] = useState<string | null>(null);

  const [auditActions, setAuditActions] = useState<string[]>([]);
  const [auditEventFilter, setAuditEventFilter] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('');

  const loadEvents = useCallback(async () => {
    if (!token) return;
    try { setEvents(await superadminApi.listEvents(token)); } catch { setError('โหลดรายชื่ออีเวนต์ไม่สำเร็จ'); }
  }, [token]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    try {
      const [ov, ev] = await Promise.all([superadminApi.getOverview(token), superadminApi.listEvents(token)]);
      setOverview(ov);
      setEvents(ev);
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        sessionStorage.removeItem('jamtick.superadminToken');
        setToken('');
        setLoginErr('Token ไม่ถูกต้อง');
      } else {
        setError('โหลดข้อมูลไม่สำเร็จ');
      }
    }
  }, [token]);

  const refreshOverview = useCallback(async () => {
    if (!token) return;
    setOverviewRefreshing(true);
    try {
      setOverview(await superadminApi.getOverview(token, true));
      setError('');
    } catch {
      setError('ตรวจสอบสถานะผู้ให้บริการไม่สำเร็จ');
    } finally {
      setOverviewRefreshing(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!token) return;
    if (tab === 'leads') superadminApi.listLeads(token).then(setLeads).catch(() => setError('โหลดข้อมูล Leads ไม่สำเร็จ'));
    if (tab === 'tokens') superadminApi.listTokens(token).then(setTokens).catch(() => setError('โหลด Token ไม่สำเร็จ'));
    if (tab === 'audit') {
      superadminApi
        .listAudit(token, { eventId: auditEventFilter || undefined, action: auditActionFilter || undefined })
        .then(setAudit)
        .catch(() => setError('โหลดบันทึกไม่สำเร็จ'));
      superadminApi.listAuditActions(token).then(setAuditActions).catch(() => {});
    }
  }, [tab, token, auditEventFilter, auditActionFilter]);

  const login = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem('jamtick.superadminToken', t);
    setLoginErr('');
    setToken(t);
  };

  const revokeToken = async (id: string) => {
    if (!window.confirm('เพิกถอน Token นี้?')) return;
    try {
      await superadminApi.revokeToken(token, id);
      setTokens(await superadminApi.listTokens(token));
    } catch {
      setError('เพิกถอนไม่สำเร็จ');
    }
  };

  const toggleEventStatus = async (e: TenantSummaryDto) => {
    const next = e.status === 'active' ? 'paused' : 'active';
    if (!window.confirm(next === 'paused' ? `หยุดการขายของ "${e.name}"?` : `เปิดขาย "${e.name}" อีกครั้ง?`)) return;
    setStatusBusy(e.id);
    try {
      await superadminApi.setEventStatus(token, e.id, next);
      await loadEvents();
    } catch {
      setError('เปลี่ยนสถานะไม่สำเร็จ');
    } finally {
      setStatusBusy(null);
    }
  };

  if (!token) {
    return (
      <div className="page admin-login-wrap">
        <h1 style={{ fontSize: 28 }}>JamTick — ROOT</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>ใส่ Superadmin token เพื่อเข้าใช้งานคอนโซลแพลตฟอร์ม</p>
        <div className="coupon-row" style={{ maxWidth: 420 }}>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="Superadmin token"
          />
          <button className="btn-ghost" onClick={login}>เข้าสู่ระบบ</button>
        </div>
        {loginErr && <div className="form-error">{loginErr}</div>}
      </div>
    );
  }

  const meta = PAGE_META[tab];
  const groups = Array.from(new Set(NAV.map((n) => n.group)));
  const storage = typeof localStorage === 'undefined' ? null : localStorage;
  // `lifecycle` was only ever an Event Admin shape, so it reads as `classic` here — one switch,
  // and the console it does not describe simply stays as it was.
  const phases = readNavMode(window.location.search, storage) === 'phases' ? superNavPhases() : null;
  const activeDest = phases?.find((d) => d.items.some((i) => i.key === tab)) ?? phases?.[0] ?? null;
  const width = tab === 'flowMaps' ? 'w-full' : 'w-wide';

  const superPageBody = (
    <>
          {error && <div className="form-error">{error}</div>}
          {tab === 'overview' && <OverviewPanel overview={overview} events={events} refreshing={overviewRefreshing} onRefresh={refreshOverview} token={token} />}
          {tab === 'homeCms' && <HomeCmsPanel events={events} token={token} onRefresh={loadAll} />}
          {tab === 'calendar' && <CalendarPanel token={token} />}
          {tab === 'leads' && <LeadsPanel leads={leads} />}
          {tab === 'events' && (
            <EventsPanel
              token={token}
              events={events}
              onOpenNew={() => setShowNewEvent(true)}
              onReset={setResetTarget}
              onToggleStatus={toggleEventStatus}
              onDelete={setDeleteTarget}
              onSaveTemplate={setTemplateTarget}
              statusBusy={statusBusy}
            />
          )}
          {tab === 'tokens' && <TokensPanel tokens={tokens} onRevoke={revokeToken} onOpenNew={() => setShowNewToken(true)} />}
          {tab === 'audit' && (
            <AuditPanel
              rows={audit}
              events={events}
              actions={auditActions}
              eventFilter={auditEventFilter}
              actionFilter={auditActionFilter}
              onEventFilterChange={setAuditEventFilter}
              onActionFilterChange={setAuditActionFilter}
            />
          )}
          {tab === 'operations' && (
            <OperationsPanel token={token} events={events} />
          )}
          {tab === 'delivery' && <DeliveryRolloutPanel token={token} events={events} />}
          {tab === 'lineOa' && <LineOaPanel token={token} events={events} />}
          {tab === 'credits' && <SuperadminCreditsPanel token={token} />}

          {tab === 'vendorLogs' && <VendorLogsPanel token={token} />}
          {tab === 'flowMaps' && (
            <Suspense fallback={<div style={{ color: 'var(--dim)', fontSize: 13 }}>กำลังโหลดตัวแสดงแผนภาพ…</div>}>
              <FlowMapsPanel />
            </Suspense>
          )}
    </>
  );

  /*
   * The five modals every shell needs, defined once.
   *
   * They used to be written inline at the end of the classic tree, below its `</main>`. The v2
   * branch returns before ever reaching that point, so in the redesigned console every control that
   * opens one — create an event, issue a token, clear sales data, delete an event, save as a
   * template — set its state and rendered nothing. No error, no network call: five buttons that
   * simply did nothing, which is exactly how it was reported.
   *
   * Bound to a name here rather than duplicated into both trees, so a sixth modal cannot arrive in
   * one shell and be forgotten in the other.
   */
  const superModals = (
    <>
      {showNewEvent && (
        <NewEventModal
          token={token}
          defaultEvent={events.find((e) => e.isDefault) ?? null}
          onClose={() => setShowNewEvent(false)}
          onCreated={loadAll}
        />
      )}
      {showNewToken && (
        <NewTokenModal
          token={token}
          events={events}
          onClose={() => setShowNewToken(false)}
          onCreated={() => { loadEvents(); superadminApi.listTokens(token).then(setTokens); }}
        />
      )}
      {resetTarget && (
        <DangerConfirmModal
          token={token}
          event={resetTarget}
          mode="reset"
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); loadAll(); }}
        />
      )}
      {deleteTarget && (
        <DangerConfirmModal
          token={token}
          event={deleteTarget}
          mode="delete"
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); loadAll(); }}
        />
      )}
      {templateTarget && (
        <SaveTemplateModal
          token={token}
          event={templateTarget}
          onClose={() => setTemplateTarget(null)}
          onSaved={() => setTemplateTarget(null)}
        />
      )}
    </>
  );

  /*
   * TICK-140 — the redesigned shell, behind `?ui=v2`. Same branch as Event Admin and for the same
   * reason: the thirteen panels below are rendered unchanged, so the frame is replaced without
   * going near what the screens do.
   *
   * No `canAccess` gate here, unlike the organizer console. This console has one role — a token
   * that reaches it opens every screen — so a permission panel would be answering a question that
   * cannot arise, and the registry says so (`roles: ['owner']` on all thirteen).
   */
  const consoleMode = readConsoleMode(window.location.search, storage);
  if (consoleMode === 'v2') {
    const destination = destinationFor('superAdmin', tab);
    const phasesV2 = navigationFor('superAdmin', 'owner');
    const activePhase = phasesV2.find((ph) => ph.items.some((i) => i.key === tab));

    return destination ? (
      <>
      <AppShell
        theme={readConsoleTheme(window.location.search, storage)}
        destination={destination}
        phaseLabel={activePhase?.label}
        width="wide"
        sidebar={{
          phases: phasesV2,
          activeKey: tab,
          onNavigate: (next) => setTab(next.key as Tab),
          console: 'superAdmin',
          brandName: 'JAMTICK',
          brandTag: 'SUPERADMIN',
          role: 'owner',
          roleLabel: 'SuperAdmin',
          icons: TAB_ICONS,
          onSignOut: () => { sessionStorage.removeItem('jamtick.superadminToken'); setToken(''); },
        }}
        topBarContext="ทุกอีเวนต์บนแพลตฟอร์ม"
        tabs={(activePhase?.items ?? []).map((d) => ({ key: d.key, label: d.navLabel }))}
        activeTab={tab}
        onSelectTab={(key) => setTab(key as Tab)}
      >
        {/*
          * Wave 4 — destinations move onto their Figma frame one at a time. A rebuilt screen
          * renders here in the v2 shell only; the classic shell keeps its panel, and both read the
          * page's own state. The list grows as each destination is rebuilt.
          */}
        {tab === 'flowMaps' ? (
          <Suspense fallback={<div style={{ color: 'var(--jt-text-tertiary)', fontSize: 13 }}>กำลังโหลดตัวแสดงแผนภาพ…</div>}>
            <FlowMapsConsolePanel destination={destination} onOpenOverview={() => setTab('overview')} />
          </Suspense>
        ) : tab === 'homeCms' ? (
          <PortalCmsDestination
            token={token} destination={destination} events={events} onRefresh={loadAll}
          />
        ) : tab === 'lineOa' ? (
          <LineOaPlatformDestination token={token} destination={destination} />
        ) : tab === 'calendar' ? (
          <GlobalCalendarDestination token={token} destination={destination} />
        ) : tab === 'credits' ? (
          <CreditOperationsDestination token={token} destination={destination} />
        ) : tab === 'delivery' ? (
          <DeliveryOperationsDestination token={token} destination={destination} events={events} />
        ) : tab === 'vendorLogs' ? (
          <VendorLogsDestination token={token} destination={destination} />
        ) : tab === 'operations' ? (
          <TransactionsDestination token={token} destination={destination} events={events} />
        ) : tab === 'events' ? (
          <EventsScreen
            destination={destination}
            events={events}
            error={error || null}
            onOpenNew={() => setShowNewEvent(true)}
            onToggleStatus={toggleEventStatus}
            onReset={setResetTarget}
            onDelete={setDeleteTarget}
            onSaveTemplate={setTemplateTarget}
            statusBusy={statusBusy}
            eventConsoleHref={(slug) => `/e/${slug}/admin?ui=v2&nav=phases`}
            /*
             * The same recovery the classic events card offered, and the reason it has to be here:
             * a superadmin who cannot read an organizer's owner token cannot go in and set the
             * event up for them, which is most of what this console is asked for.
             *
             * Fetched per event and only on request — never held in this component's state, never
             * in the list payload — so the value exists for as long as `CredentialReveal` shows it
             * and no longer.
             */
            revealOwnerToken={async (e) => {
              const rows = await superadminApi.listTokens(token, e.id);
              return rows.find((r) => r.role === 'owner' && r.active && r.token)?.token ?? null;
            }}
            eventLinks={(e) => [
              { label: 'หน้าร้าน', href: `${window.location.origin}/e/${e.slug}` },
              { label: 'คอนโซลผู้จัด', href: `${window.location.origin}/e/${e.slug}/admin` },
              { label: 'จุดเช็กอิน', href: `${window.location.origin}/e/${e.slug}/staff/checkin` },
            ]}
            /*
             * TICK-051's per-event Thunder/EasySlip keys, which had no way in at all under `?ui=v2`
             * — an event provisioned in the new console could not be given a slip verifier, and an
             * event with no key of its own has that provider off with no platform key behind it.
             *
             * `key` on the event id so the card is a new one per organizer: its state is a set of
             * secrets, and the one thing it must never do is show one event's under another's name.
             */
            vendorKeys={(e) => (
              <EventVendorKeysDestination
                key={e.id} token={token} eventId={e.id} eventName={e.name || e.slug}
                /* The row behind this drawer carries a badge derived from the same state. */
                onChanged={loadAll}
              />
            )}
          />
        ) : tab === 'overview' ? (
          <PlatformOverviewScreen
            destination={destination}
            overview={overview}
            events={events}
            error={error || null}
            onRefresh={refreshOverview}
            refreshing={overviewRefreshing}
            onOpenAudit={() => setTab('audit')}
            onToggleProvider={(id, enabled) => superadminApi.toggleProvider(token, id, enabled)}
          />
        ) : tab === 'leads' ? (
          <LeadsScreen destination={destination} leads={leads} error={error || null} />
        ) : tab === 'tokens' ? (
          <AccessTokensScreen
            destination={destination}
            tokens={tokens}
            error={error || null}
            onRevoke={revokeToken}
            onCreate={() => setShowNewToken(true)}
          />
        ) : tab === 'audit' ? (
          <AuditLogScreen
            destination={destination}
            rows={audit}
            events={events}
            actions={auditActions}
            eventFilter={auditEventFilter}
            actionFilter={auditActionFilter}
            onEventFilterChange={setAuditEventFilter}
            onActionFilterChange={setAuditActionFilter}
            error={error || null}
          />
        ) : superPageBody}
      </AppShell>
      {/* A sibling of the shell, not a child: `.sa-modal-overlay` is `position: fixed`, and keeping
          it outside the console's grid keeps it out of reach of any containing block the layout
          introduces later. */}
      {superModals}
      </>
    ) : null;
  }


  return (
    <div className="admin-shell" data-theme={readThemeMode(window.location.search, storage)}>
      <aside className={`admin-sidebar${navCollapsed ? ' collapsed' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-row">
            {/* Dropped from the tree rather than hidden in CSS: this block carries inline styles,
                which outrank any `display: none` a `.collapsed` rule could apply. */}
            {!navCollapsed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <div className="name">JamTick</div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.14em', color: 'var(--fg)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px' }}>
                  ROOT
                </span>
              </div>
            )}
            <button
              className="admin-sidebar-toggle"
              onClick={() => setNavCollapsed((v) => !v)}
              title={navCollapsed ? 'ขยายเมนู' : 'ย่อเมนู'}
              aria-label={navCollapsed ? 'ขยายเมนู' : 'ย่อเมนู'}
              aria-expanded={!navCollapsed}
            >
              <IconSidebar />
            </button>
          </div>
          <div className="tag">SUPERADMIN CONTROL</div>
        </div>

        {/* One flat group of destinations — no headings, because a heading over a single button is
            the duplication this shape exists to remove. */}
        {phases && (
          <div className="admin-nav-group">
            {phases.map((d) => {
              const here = activeDest?.key === d.key;
              const badge = d.key === 'events' ? events.length
                : d.key === 'access' ? leads.length + (overview?.activeTokenCount ?? 0)
                  : 0;
              return (
                <button
                  key={d.key}
                  aria-label={d.label}
                  title={d.label}
                  className={`admin-nav-btn ${here ? 'active' : ''}`}
                  // Re-entering the destination you are already in must not throw away the tab you
                  // are on.
                  onClick={() => { if (!here) setTab(d.items[0].key); }}
                >
                  {d.icon}
                  <span className="grow">{d.label}</span>
                  {badge > 0 && <span className="admin-nav-badge">{badge}</span>}
                </button>
              );
            })}
          </div>
        )}

        {!phases && groups.map((g) => (
          <div key={g}>
            <div className="admin-nav-label">{g}</div>
            <div className="admin-nav-group">
              {NAV.filter((n) => n.group === g).map((n) => (
                <button key={n.key} aria-label={n.label} className={`admin-nav-btn ${tab === n.key ? 'active' : ''}`} onClick={() => setTab(n.key)}>
                  {n.key === 'overview' && <IconGrid />}
                  {n.key === 'events' && <IconLayers />}
                  {n.key === 'homeCms' && <IconHome />}
                  {n.key === 'calendar' && <IconCalendar />}
                  {n.key === 'lineOa' && <IconChat />}
                  {n.key === 'leads' && <IconPhone />}
                  {n.key === 'tokens' && <IconKey />}
                  {n.key === 'audit' && <IconAudit />}
                  {n.key === 'operations' && <IconCheck />}
                  {n.key === 'vendorLogs' && <IconServer />}
                  {n.key === 'flowMaps' && <IconFlow />}
                  <span className="grow">{n.label}</span>
                  {n.key === 'events' && events.length > 0 && <span className="admin-nav-badge">{events.length}</span>}
                  {n.key === 'leads' && leads.length > 0 && <span className="admin-nav-badge">{leads.length}</span>}
                  {n.key === 'tokens' && overview && overview.activeTokenCount > 0 && <span className="admin-nav-badge">{overview.activeTokenCount}</span>}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={{ flex: 1 }}></div>
        <div className="admin-sidebar-foot">
          <div className="admin-user-info">
            <div className="admin-avatar">SA</div>
            <div className="admin-user-label">แพลตฟอร์ม · Superadmin</div>
          </div>
          <button
            className="admin-logout-btn"
            title="ออกจากระบบ"
            onClick={() => { sessionStorage.removeItem('jamtick.superadminToken'); setToken(''); }}
          >
            <IconLogout />
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className={`admin-page-head ${activeDest && activeDest.items.length > 1 ? 'has-sections' : ''}`}>
          {/* TICK-134 — same width token as the body below, so the two share a left edge. */}
          <div className={`admin-page-head-inner ${width}`}>
            <div>
              {/* TICK-133 — the menu group above the title; hidden unless the unified theme is on. */}
              <div className="eyebrow">{activeDest?.label ?? NAV.find((n) => n.key === tab)?.group}</div>
              <h1>{meta.title}</h1>
              <div className="sub">{meta.sub}</div>
            </div>
            {(tab === 'overview' || tab === 'events') && (
              <button className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px' }} onClick={() => setShowNewEvent(true)}>
                <IconPlus /> สร้างอีเวนต์ใหม่
              </button>
            )}
          </div>
          {/* A destination holding one screen shows no tab row: a single tab is a label pretending
              to be a control. */}
          {activeDest && activeDest.items.length > 1 && (
            <div className={`admin-sections-inner ${width}`}>
              <div className="admin-sections" role="tablist" aria-label={activeDest.label}>
                {activeDest.items.map((s) => (
                  <button
                    key={s.key}
                    role="tab"
                    aria-selected={tab === s.key}
                    className={`admin-section-btn ${tab === s.key ? 'active' : ''}`}
                    onClick={() => setTab(s.key)}
                  >
                    {s.label}
                    {s.key === 'leads' && leads.length > 0 && <span className="admin-subtab-count">{leads.length}</span>}
                    {s.key === 'tokens' && overview && overview.activeTokenCount > 0 && (
                      <span className="admin-subtab-count">{overview.activeTokenCount}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={`admin-page-body ${width}`}>
          {superPageBody}
        </div>
      </main>

      {superModals}
    </div>
  );
}
